import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { COLS, ROWS, TOTAL_LEVELS, getLevel } from "../lib/levels";
import { Sounds, startMusic, stopMusic, startTensionLayer, stopTensionLayer, useSound, setSpookyPack } from "../lib/sound";
import { Haptic } from "../lib/haptics";
import { DEFAULT_SKIN } from "../lib/comet";
import apiClient from "../lib/api";
import * as artifactPowers from "./artifactPowers";
import { maybeSpawnPumpkinBomb, tickAndDrawPumpkinBombs, clearPumpkinBombs } from "./pumpkinBombs";
import { maybeSpawnCandy, tickAndDrawCandy, clearCandy } from "./candyDrops";
import {
  W,
  H,
  BLOCK_PAD,
  GRID_TOP,
  GRID_LEFT,
  BLOCK_W,
  BLOCK_H,
  PADDLE_Y,
  PADDLE_H,
  BASE_PADDLE_W,
  BALL_RADIUS,
  TIER_COLORS,
  TIER_HP,
  POWERUP_CHANCE,
  MOONSTONE_DROP_CHANCE,
  MOONSTONE_FALL_SPEED,
  MOONSTONE_RADIUS,
  MOONSTONE_SHOP,
  MOONSTONE_POWERUP_DEFS,
} from "./engine/constants";
import { pickWeightedPowerup } from "./engine/powerups";
import {
  drawBackground,
  drawBlock,
  drawPaddle,
  drawWorm,
  drawPowerup,
  drawParticles,
  drawSlowOverlay,
  drawBlackHole,
  drawMoonstone,
  drawPlasmaBolt,
  drawSpaceWorm,
  drawFxOverlays,
  drawFxFlash,
  drawStoneTicks,
  drawCometStreaks,
} from "./engine/draw";
import useGameInput from "./hooks/useGameInput";
import { HudBlock, ArtifactHudChip, AutoSavedPill } from "./ui/GameHud";
import { ArtifactsDisabledBanner, TournamentOpponentHud } from "./CompetitiveHud";
import { ActivePowerupsHud } from "./PowerupHud";
import { PowerupTray } from "./PowerupTray";
import { WalletStreakStrip } from "./WalletStreakStrip";
import { BossBanner } from "./BossBanner";
import { PauseOverlay } from "./PauseOverlay";
import { FullscreenHud } from "./FullscreenHud";
import { MoonstoneTipBanner, HotkeyTipBanner } from "./ui/OnboardingBanners";
import { GameOverOverlay, WinOverlay } from "./LevelSummaryModals";
import { POWERUP_INFO, DEFERRED_STARTERS } from "./engine/catalog";
import PowerupLegendModal from "./ui/PowerupLegendModal";
import { BossEntranceCinematic, BossDefeatedCinematic } from "./ui/BossCinematics";
import { BossRageBanner } from "./ui/BossRageBanner";
import { WaveBanner, ShapeCallout } from "./ui/WaveOverlays";
import LaunchHint from "./ui/LaunchHint";
import GameControlsBar from "./ui/GameControlsBar";
import ComboHud from "./ui/ComboHud";

// `KeyBadge` (the keycap pill) lives in `./ui/KeyBadge.jsx`. It's now
// only consumed inside the extracted onboarding banners + FullscreenHud,
// so GameCanvas itself no longer needs to import it directly.

export default function GameCanvas({ level, onComplete, onExit, bestScore, levelOverride, isDaily = false, fsTargetRef, endless = false, endlessWave = 0, endlessStartLives = null, dailyStartLives = null, dailyStartScore = null, campaignStartLives = null, campaignStartScore = null, onWaveClear, onEndlessRestart, onFail, mode = "campaign", onAdvance = null, forceBallSkin = null, opponentLive = null, onCarnageUpdate = null }) {
  const { muted } = useSound();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef(null);
  const keysRef = useRef({});
  const mouseXRef = useRef(null);
  const wrapperRef = useRef(null);
  const metricsRef = useRef(null);
  const seenPowerupsRef = useRef(new Set());

  // Combo / Streaker — tracks consecutive brick destroys within a 1.2s
  // window. `comboRef` is the source of truth inside the rAF loop;
  // `comboCount` is mirrored to React state so the on-canvas HUD can
  // animate. `bestCombo` is reported in the run-complete payload to
  // drive the Comet Streaker achievement family.
  const COMBO_WINDOW_MS = 1200;
  const comboRef = useRef({ count: 0, lastAt: 0, runBest: 0 });
  const [comboCount, setComboCount] = useState(0);

  const [lives, setLives] = useState(3);
  // Mirror of `lives` state for use inside the rAF render loop, where
  // closure capture would otherwise read a stale value.
  const livesRef = useRef(3);
  useEffect(() => { livesRef.current = lives; }, [lives]);
  const [score, setScore] = useState(0);
  const [paused, setPaused] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [activePowerups, setActivePowerups] = useState([]);
  const [hudTick, setHudTick] = useState(0);
  const [ballsLeft, setBallsLeft] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  // Free-text filter over the Legend modal. Matched case-insensitively
  // against label + desc + kind key. Also filters the Bone Coin hint so
  // empty results feel honest.
  const [legendQuery, setLegendQuery] = useState("");
  // Transient wave banner ({ kind, label, color }) — shown for 1.8s when
  // an Endless wave kicks off with skeletons or a boss Leviathan, so the
  // pilot feels the tempo shift. `null` when hidden.
  const [waveBanner, setWaveBanner] = useState(null);
  // Stuck-ball drought hint — when the pilot has had a stuck ball on
  // the paddle for >8s on a level past the tutorial (level > 1), gently
  // re-surface the "click / tap / space to launch" pill. Helps pilots
  // who lose focus mid-session or pick the game back up after a break.
  // `stuckSinceRef` tracks the timestamp the ball first stuck; the
  // visible state flips true after 8s and clears the moment the ball
  // is launched. Avoids spamming pilots — only fires after a real lull.
  const stuckSinceRef = useRef(null);
  const [stuckDroughtHint, setStuckDroughtHint] = useState(false);
  // Boss cinematic — full-screen title card that fades in for ~2.4s on
  // every regular-play Leviathan level (every 10/20, excluding sector
  // bosses at /500). Shape: { kind: "small" | "giant", label, levelName,
  // startedAt, durationMs }. `null` when no intro is active.
  // The cinematic auto-clears after `durationMs`; clicking/space also
  // dismisses it (without launching the ball — tapping again launches).
  // Endless mode keeps its own wave banner and skips this entirely.
  const [bossCinematic, setBossCinematic] = useState(null);
  const bossCinematicRef = useRef(null);
  // Boss DEFEATED cinematic — mirror of the intro, fired when a
  // regular-play Leviathan dies. Same shape as `bossCinematic` but
  // with a green/gold "LEVIATHAN SLAIN" / "APEX DEFEATED" message and
  // shorter duration (~1.6s). Auto-clears.
  const [bossDefeatedCinematic, setBossDefeatedCinematic] = useState(null);
  const bossDefeatedCinematicRef = useRef(null);
  // Boss RAGE MODE — cosmetic-only flag that surfaces a magenta edge
  // vignette + "RAGE MODE" pill whenever any non-skeleton Space Worm
  // (small/giant Leviathan) drops below 30% of its maxHp. Polled from
  // the rAF-mutated stateRef every 200ms so we don't have to thread an
  // extra setState through the hot collision path. Auto-clears the
  // moment the last raging worm dies or all worms are above the
  // threshold.
  const [bossRageActive, setBossRageActive] = useState(false);
  // Boss camera zoom — `performance.now()` timestamp when the boss
  // level mounted. Drives a brief 1.06× → 1.0× pull-out animation
  // so the Anglerfish is shown up close during the entrance cinematic
  // and slowly retreats to playable scale by the time the pilot takes
  // control. `0` when no zoom is active. Only set on regular-play
  // Leviathan levels (skipped in Endless).
  const bossZoomStartRef = useRef(0);
  // One-time "Hotkeys Available" tooltip shown on the first fullscreen
  // entry of a pilot's career. localStorage-gated so it never pesters
  // repeat visitors; cleared + hidden on dismiss.
  const [showHotkeyTip, setShowHotkeyTip] = useState(false);
  // One-time "First Moonstone" tip — fires on the very first moonstone
  // catch in Endless mode. Explains the economy without a forced
  // tutorial. Same localStorage pattern as the hotkey tip.
  const [showMoonstoneTip, setShowMoonstoneTip] = useState(false);
  // Endless mode currency + active "moonstone" power-up timers. The shop is
  // surfaced from the pause overlay so the pilot can spend mid-run. Refs
  // mirror the state for the raf loop without forcing re-renders.
  // The wallet is PERSISTENT (server-side, capped at 5,000). On run-mount we
  // pull the balance from /moonstones/me; the in-run shop spends from the
  // same pool. Deltas are tracked in refs and reconciled on run-over.
  const [moonstones, setMoonstones] = useState(0);
  const moonstonesRef = useRef(0);
  useEffect(() => { moonstonesRef.current = moonstones; }, [moonstones]);

  // Boss RAGE MODE poll — 200ms interval scans the live spaceWorms
  // array for any non-skeleton Leviathan below 30% HP. Fires while the
  // run is mid-flight (no paused / gameOver / won gates needed since
  // the banner itself respects them via the `hidden` prop). Cleared on
  // unmount.
  useEffect(() => {
    const tick = () => {
      const worms = stateRef.current?.spaceWorms || [];
      let rage = false;
      for (const w of worms) {
        if (!w || w.kind === "skeleton") continue;
        if (!w.maxHp || w.hp <= 0) continue;
        if (w.hp / w.maxHp < 0.3) { rage = true; break; }
      }
      setBossRageActive((prev) => (prev === rage ? prev : rage));
    };
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, []);
  const stonesCollectedRef = useRef(0);
  const stonesSpentRef = useRef(0);
  const [walletCfg, setWalletCfg] = useState({
    cap: 100,
    perks: [],
    activeSkin: "default",
    skinPalette: null,
    pendingStarters: [],
  });
  const walletCfgRef = useRef(walletCfg);
  useEffect(() => { walletCfgRef.current = walletCfg; }, [walletCfg]);
  const [moonstoneFx, setMoonstoneFx] = useState({
    phaseUntil: 0,
    dilationUntil: 0,
    plasmaUntil: 0,
    plasmaNextFireAt: 0,
    // Plasma cadence — paddle re-fires every `plasmaFireInterval` ms.
    // 1000ms is the moonstone-shop default; Endless milestones override
    // this to 500ms for the boosted "twin barrel" cadence.
    plasmaFireInterval: 1000,
    // Number of bolts per fire. 1 = single vertical bolt (shop default).
    // 3 = a spread fan (left/center/right) for boosted milestone firings.
    plasmaBoltsPerFire: 1,
    shieldArmed: false,
  });
  const moonstoneFxRef = useRef(moonstoneFx);
  useEffect(() => { moonstoneFxRef.current = moonstoneFx; }, [moonstoneFx]);
  const [shopFlashId, setShopFlashId] = useState(null);
  // Tracks which starter ids the engine actually consumed so the run-over
  // submit can clear them from the server's pending_starters list.
  const startersUsedRef = useRef([]);
  // Bomb+Chain synergy — transient lightning arcs spawned when the
  // Bomb artifact fires. Each entry: { x1, y1, x2, y2, bornAt, ttlMs }.
  // Drawn from `drawFrame` and self-pruned each render pass.
  const bombArcsRef = useRef([]);
  // Local accumulator for bricks killed by the green Worm Swarm. Flushed
  // to /achievements/worm-kills the moment the active swarm fully expires
  // so the Worm Tamer (100-kills) achievement can unlock.
  const wormKillsRef = useRef(0);
  // Activation flash burst on power-up buy — `{ color, until }` in gameNow
  // milliseconds. Painted in the draw loop and naturally expires.
  const fxFlashRef = useRef(null);
  // Comet Storm choreography: set by the wave-10 trigger. `streaks` is an
  // array of animated diagonal trails drawn for 400ms; `destroyAt` is the
  // gameNow timestamp at which the loop executes the 30% brick destruction
  // (so the meteor shower lands *on* the bricks rather than after a lag).
  const cometStormRef = useRef({ streaks: [], destroyAt: 0 });
  // Camera-shake: a brief horizontal+vertical jitter applied to the canvas
  // element on impact moments (Comet Storm landing for now). Stored as
  // `{ until, magnitude }` and applied each frame via a CSS transform on
  // the canvas DOM node directly — never touches React state so it adds
  // zero render cost.
  const cameraShakeRef = useRef({ until: 0, magnitude: 0 });
  // Lunar Streak multiplier: catch 3 stones within 4s to enter a "hot"
  // window where the next 5 catches award +2 stones each (golden tick).
  // Tracked entirely in a ref so the raf loop can update without
  // triggering React re-renders. Reset on game-over via metricsRef
  // initialisation.
  // Also tracks the in-run BEST chain — `currentChain` is the rolling
  // count of consecutive catches within 4s, `bestChainThisRun` is the
  // max it ever reached. Both are submitted in the run-over metrics so
  // the server can persist `longest_lunar_streak` and award the Cascade
  // Master achievement at 10.
  const stoneStreakRef = useRef({
    timestamps: [],
    bonusLeft: 0,
    currentChain: 0,
    bestChainThisRun: 0,
  });

  // Boost Streak — counts CONSECUTIVE milestone-wave clears (wave % 5 === 0)
  // without losing a life. Each tier awards a flat bonus on clear: 1000,
  // 1500, 2000, 2500, 3000 pts at streaks 1–5+. Resets on any life loss
  // (with a "STREAK BROKEN" toast when the loss costs something). Endless
  // only — campaign play never reaches a milestone via this mechanic.
  // Tracked in a ref so the raf loop + life-loss handler can read/write
  // without forcing renders.
  const boostStreakRef = useRef({ count: 0, best: 0 });
  // Boosted-plasma activation timestamp — when an Endless milestone
  // fires the plasma cannon at boosted cadence, we record gameNow()
  // here so the rAF loop can paint a 18-particle magenta ring around
  // the paddle on its next tick. Reading + clearing happens inside
  // the loop so we get spawnParticles in lexical scope.
  const boostedPlasmaActivationAt = useRef(0);
  // Flawless Hunter trophy claim — once-per-run guard so the POST only
  // fires the first time both gates are crossed (wave>=30 cleared AND
  // best_boost_streak>=5). Reset on `restart` along with other per-run state.
  const flawlessClaimedThisRunRef = useRef(false);
  // Stingray Carnage Counter — local mirror of the lifetime kill count
  // + next-tier target. Pushed up to the Endless page via
  // onCarnageUpdate so the sidebar chip can render real-time progress.
  const [carnageCount, setCarnageCount] = useState(null);
  const [carnageNextTier, setCarnageNextTier] = useState(null);
  useEffect(() => {
    if (typeof onCarnageUpdate === "function" && carnageCount !== null) {
      onCarnageUpdate({ count: carnageCount, next_tier: carnageNextTier });
    }
  }, [carnageCount, carnageNextTier, onCarnageUpdate]);
  // Endless-mode blood splatter persistence — survives wave transitions
  // so the "trophy wall" of carnage builds up across an entire run.
  // Wiped on hard restart only.
  const endlessBloodSplatsRef = useRef([]);

  // AUTO-SAVED pill — stamped each time a checkpoint POST resolves
  // successfully. UI fades after 1600ms via setTimeout. Lives as
  // React state so the HUD pill re-renders on stamp.
  const [autoSavedAt, setAutoSavedAt] = useState(0);

  // Artifacts (paddles + balls with upgradable powers). Fetched
  // once on mount, mutated by the engine each frame for per-life state
  // (aegis charges, bomb hit counter, chain-window timestamps). Skipped
  // entirely in competitive modes (`tournament`, `wager`).
  const artifactsRef = useRef(artifactPowers.defaultArtifactState());
  const [artifactHud, setArtifactHud] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get("/artifacts");
        if (cancelled) return;
        artifactsRef.current = artifactPowers.hydrateFromAPI(r.data, mode);
        setArtifactHud(artifactPowers.summary(artifactsRef.current));
        // First-time discovery toast — surfaces what each equipped
        // artifact actually does on the very first run after equipping
        // it. Persisted in localStorage so the same artifact never
        // toasts twice; pilots can re-trigger by equipping a fresh one.
        // Skipped entirely in competitive (tournament/wager) modes.
        if (!artifactsRef.current.competitive) {
          const slots = [
            { state: artifactsRef.current.paddle, slot: "paddle" },
            { state: artifactsRef.current.ball, slot: "ball" },
          ];
          slots.forEach(({ state, slot }, idx) => {
            if (!state) return;
            const seenKey = `artifact-tutorial:${state.id}`;
            try {
              if (window.localStorage.getItem(seenKey) === "1") return;
              window.localStorage.setItem(seenKey, "1");
            } catch {
              return;  // private mode — skip toast
            }
            const lvl = slot === "paddle"
              ? artifactsRef.current.paddleLevel
              : artifactsRef.current.ballLevel;
            const tierLabel = state.tiers[Math.max(0, lvl - 1)].label;
            // Stagger so paddle + ball don't stack on top of each other.
            setTimeout(() => {
              toast(
                `${state.icon}  ${state.name} active`,
                {
                  description: `${state.desc} · L${lvl} · ${tierLabel}`,
                  duration: 6500,
                },
              );
            }, 1500 + idx * 800);
          });
        }
      } catch {
        // Silent — pilot just plays without artifact effects.
      }
    })();
    return () => { cancelled = true; };
  }, [mode]);
  // Periodic HUD refresh — 250ms tick reads the per-life mutable state
  // from artifactsRef (aegis charges, bomb hits, phase charges, laser
  // cooldown). Cheap object recreation, no diffing needed — React
  // batches the re-render with other state updates.
  useEffect(() => {
    if (!artifactHud) return undefined;
    const id = setInterval(() => {
      setArtifactHud(artifactPowers.summary(artifactsRef.current));
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(artifactHud)]);

  // Game-time clock that FREEZES during pause. All moonstone power-up
  // timers (phaseUntil, dilationUntil, plasmaUntil, plasmaNextFireAt) are
  // stored against this clock instead of raw performance.now() — otherwise
  // a power-up bought from the pause-menu shop would burn through its
  // duration while the pause overlay is still open. `gameNow()` returns
  // performance.now() minus accumulated paused milliseconds; while paused
  // it returns the pause-start time so timers tick zero seconds.
  const pauseClockRef = useRef({ pausedAt: 0, accumulated: 0 });
  const gameNow = useCallback(() => {
    const real = performance.now();
    const c = pauseClockRef.current;
    if (c.pausedAt > 0) return c.pausedAt - c.accumulated;
    return real - c.accumulated;
  }, []);
  useEffect(() => {
    if (paused) {
      // Just paused — capture the moment so subsequent gameNow() calls
      // freeze on it and the accumulated counter only updates on resume.
      pauseClockRef.current.pausedAt = performance.now();
    } else if (pauseClockRef.current.pausedAt > 0) {
      // Resumed — fold the just-elapsed pause window into accumulated.
      pauseClockRef.current.accumulated += performance.now() - pauseClockRef.current.pausedAt;
      pauseClockRef.current.pausedAt = 0;
    }
  }, [paused]);
  // Shape callout chip — shows the procedural shape(s) name for ~3.5s when the
  // level first mounts so pilots recognise what formation they're shattering.
  const [shapeCalloutVisible, setShapeCalloutVisible] = useState(true);

  const levelCfg = useMemo(
    () => levelOverride || getLevel(level),
    [levelOverride, level],
  );

  useEffect(() => {
    setShapeCalloutVisible(true);
    const t = setTimeout(() => setShapeCalloutVisible(false), 3500);
    return () => clearTimeout(t);
  }, [level, levelOverride]);

  // Stuck-ball drought watcher — runs at 4Hz only while a stuck ball
  // exists on the paddle. After an 8-second drought (any level past
  // the tutorial), gently re-surfaces the "click / tap / space to
  // launch" hint. Clears the moment any ball is launched.
  useEffect(() => {
    if (paused || gameOver || won || bossCinematic) return undefined;
    const t = setInterval(() => {
      const s = stateRef.current;
      const stuckBall = s?.balls?.some?.((b) => b.stuck);
      if (!stuckBall) {
        stuckSinceRef.current = null;
        setStuckDroughtHint((prev) => (prev ? false : prev));
        return;
      }
      if (stuckSinceRef.current == null) stuckSinceRef.current = Date.now();
      const dwell = Date.now() - stuckSinceRef.current;
      if (dwell > 8000) setStuckDroughtHint((prev) => (prev ? prev : true));
    }, 250);
    return () => clearInterval(t);
  }, [paused, gameOver, won, bossCinematic]);

  const [activeSkin, setActiveSkin] = useState("default");
  const [paddleAccent, setPaddleAccent] = useState(null);
  const [ballSkin, setBallSkin] = useState(DEFAULT_SKIN);
  const ballSkinRef = useRef(DEFAULT_SKIN);

  // Halloween 2026 event bundle — client-side effect flags. When the
  // pilot owns the bundle AND has equipped the halloween-comet skin,
  // pumpkin bombs periodically drop from the ball trail and the
  // spooky sound pack replaces standard SFX. Ownership is fetched
  // once on mount; equipped-skin state falls out of ballSkin below.
  const halloweenOwnedRef = useRef(false);
  const pumpkinBombsRef = useRef([]);
  // Trick-Or-Treat Daily — active-flag for October (any year). When on
  // AND the current game mode is `isDaily`, candy sprites drift down
  // as a decorative overlay. Populated by /api/trick-or-treat/status.
  const trickOrTreatActiveRef = useRef(false);
  const candyDropsRef = useRef([]);
  useEffect(() => {
    let cancel = false;
    apiClient
      .get("/halloween/status")
      .then((r) => {
        if (cancel) return;
        halloweenOwnedRef.current = !!r.data.owned;
        // Re-evaluate spooky pack in case skin was already loaded.
        try {
          setSpookyPack(halloweenOwnedRef.current && ballSkinRef.current?.id === "halloween-comet");
        } catch { /* noop */ }
      })
      .catch(() => {});
    return () => { cancel = true; try { setSpookyPack(false); } catch { /* noop */ } };
  }, []);

  // Fetch Trick-Or-Treat status once so we know whether to render
  // candy drops during daily runs in October.
  useEffect(() => {
    if (!isDaily) return;
    let cancel = false;
    apiClient
      .get("/trick-or-treat/status")
      .then((r) => { if (!cancel) trickOrTreatActiveRef.current = !!r.data.active; })
      .catch(() => {});
    return () => { cancel = true; };
  }, [isDaily]);

  useEffect(() => {
    ballSkinRef.current = ballSkin;
    // Toggle the Halloween spooky sound pack whenever the equipped
    // ball skin changes. Requires bundle ownership so guests can't
    // sneak the seasonal SFX by URL-manipulating the skin id.
    try {
      setSpookyPack(halloweenOwnedRef.current && ballSkin?.id === "halloween-comet");
    } catch { /* noop */ }
  }, [ballSkin]);

  // Load user's active paddle + ball skin once
  useEffect(() => {
    let cancel = false;
    apiClient
      .get("/cosmetics")
      .then((r) => {
        if (cancel) return;
        setActiveSkin(r.data.active || "default");
        setPaddleAccent(r.data.paddle_accent || null);
      })
      .catch(() => {});
    apiClient
      .get("/ball-skins")
      .then((r) => {
        if (cancel) return;
        // Competitive fairness — when the parent forces a neutral comet
        // skin (PvP / tournament play), ignore the pilot's equipped one.
        // Skin still drives only visuals — physics never depend on it —
        // but a forced neutral ball means leaderboard screenshots and
        // bracket replays don't get padded with "intimidation skins"
        // that some pilots find distracting.
        if (forceBallSkin) {
          const forced =
            (r.data.skins || []).find((s) => s.id === forceBallSkin) || null;
          setBallSkin(forced || DEFAULT_SKIN);
          return;
        }
        const active = (r.data.skins || []).find((s) => s.id === r.data.active);
        if (!active) return;
        // Honour the user's trail-color override if present so the in-game
        // ball matches the live preview on the Skins page.
        const tc = r.data.trail_color;
        if (tc) {
          const flame = Array.isArray(active.flame) ? [...active.flame] : ["#ff007f", "#ff007f"];
          flame[0] = tc;
          setBallSkin({ ...active, flame });
        } else {
          setBallSkin(active);
        }
      })
      .catch(() => {
        // Even on fetch failure: when force-ball is set, fall back to
        // the bundled neutral default so we never accidentally surface
        // the wrong skin in a competitive run.
        if (forceBallSkin && !cancel) setBallSkin(DEFAULT_SKIN);
      });
    // Endless: load the persistent moonstone wallet, owned perks, and any
    // pending run-starters. The starters are auto-applied on run mount and
    // the consumption list is tracked in startersUsedRef so the run-over
    // submit can clear them from the server.
    // Load the persistent moonstone wallet, owned perks, and any pending
    // run-starters. Runs in BOTH regular play and Endless — regular play
    // needs `pending_starters` so multi-mode starters (e.g. Worm Swarm)
    // can surface a launch button. Endless additionally auto-applies the
    // mode-specific starters (extra-life loadout, pre-armed shield, wave
    // triggers) on mount.
    Promise.all([
      apiClient.get("/moonstones/me").then((r) => r.data).catch(() => null),
      apiClient.get("/moonstones/store").then((r) => r.data).catch(() => null),
    ]).then(([me, store]) => {
      if (cancel || !me) return;
      const skinsCatalog = (store?.skins) || [];
      const activeSkinDoc = skinsCatalog.find((s) => s.id === me.active_skin);
      const skinPalette = activeSkinDoc?.palette || null;
      const skinName = activeSkinDoc?.name || "Opal";
      setMoonstones(Number(me.balance || 0));
      setWalletCfg({
        cap: Number(me.cap || 100),
        perks: Array.isArray(me.owned_perks) ? me.owned_perks : [],
        activeSkin: me.active_skin || "default",
        skinPalette,
        skinName,
        ownedSkins: Array.isArray(me.owned_skins) ? me.owned_skins : ["default"],
        skinsCatalog,
        pendingStarters: Array.isArray(me.pending_starters) ? me.pending_starters : [],
        wormTamerUnlocked: !!me.worm_tamer_unlocked,
      });
      if (!endless) return; // regular play: we only need pendingStarters for the launch button
      // Apply IMMEDIATE pending starters (consumed at run mount). The
      // wave-N-trigger and worm-swarm starters are deferred — they fire
      // only when explicitly triggered/reached. Marking them here would
      // cause the server to consume them on run-over submit even if the
      // pilot never used them. Keep this list in sync with DEFERRED_STARTERS.
      const used = [];
      let bonusLives = 0;
      let preShield = false;
      for (const sid of me.pending_starters || []) {
        if (sid === "extra-life-loadout") { bonusLives += 1; used.push(sid); }
        else if (sid === "pre-armed-shield") { preShield = true; used.push(sid); }
        // wave-N-trigger / worm-swarm starters intentionally NOT pushed.
      }
      startersUsedRef.current = used;
      if (bonusLives > 0) setLives((l) => l + bonusLives);
      if (preShield) {
        setMoonstoneFx((fx) => ({ ...fx, shieldArmed: true }));
      }
    });
    return () => {
      cancel = true;
    };
  }, [endless, forceBallSkin]);

  const buildBlocks = useCallback(() => {
    const grid = levelCfg.grid;
    const blocks = [];
    // Boss levels: the cluster bricks (rows 0..4) gently oscillate side-to-side
    // as a unit during the loop. Defensive flank bricks at row 6+ stay anchored
    // so the fortress feels solid.
    const isBoss = !!levelCfg.boss;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const tier = grid[r]?.[c] || 0;
        if (!tier) continue;
        const inBossCluster = isBoss && r <= 4;
        const baseX = GRID_LEFT + c * BLOCK_W + BLOCK_PAD / 2;
        blocks.push({
          x: baseX,
          // Save the canonical anchor X so the oscillation can offset
          // around it without drift; only used by boss-cluster bricks.
          baseX,
          y: GRID_TOP + r * BLOCK_H + BLOCK_PAD / 2,
          w: BLOCK_W - BLOCK_PAD,
          h: BLOCK_H - BLOCK_PAD,
          tier,
          hp: TIER_HP[tier],
          // Bricks no longer rotate (May 2026 design pass — the user
          // requested static space-shape layouts so each level reads
          // as a coherent piece of cosmic art instead of a spinning
          // chaos field). `rot` is kept at 0 — `drawBlock` still
          // reads it for the deterministic sparkle-pattern seed but
          // it doesn't tilt the brick anymore.
          rot: 0,
          rotSpeed: 0,
          bossPart: inBossCluster,
        });
      }
    }
    return blocks;
  }, [levelCfg]);

  const initState = useCallback(() => {
    const speed = levelCfg.ballSpeed;
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.6;
    // Space Worm boss spawning — small worm every 10 levels, giant every
    // 20 levels (giant takes precedence on shared 20-multiples). 500-level
    // multiples already host the cinematic sector bosses, so we skip them
    // to avoid stepping on that flow.
    //
    // In endless mode we use the current wave number for the same cadence:
    // skeleton swarm every 5 waves, small Leviathan every 10, giant every
    // 20. Gives the endless run the same "mini-boss beat" that regular
    // play has without interfering with the existing wave difficulty ramp.
    const lvlForWorm = endless ? endlessWave : level;
    let spaceWormKind = null;
    if (lvlForWorm > 0 && lvlForWorm % 500 !== 0) {
      if (lvlForWorm % 20 === 0) spaceWormKind = "giant";
      else if (lvlForWorm % 10 === 0) spaceWormKind = "small";
      // Skeleton Swarm — every 5th level that ISN'T already a small/giant
      // Leviathan or sector boss. Spawns a trio of bone-white mini-bosses
      // so 5/15/25/35/... feel like a fight without competing with the
      // Leviathan cadence at 10/20.
      else if (lvlForWorm % 5 === 0) spaceWormKind = "skeleton-swarm";
    }
    const spaceWorms = [];
    // Tracks the perfect-clear bonus for the every-5 Skeleton Swarm. We
    // only drop a Bone Coin when ALL initially-spawned skeletons die on
    // the SAME life — losing a life resets the kill counter so the bonus
    // is a real skill check, not a grind. Counters live on stateRef so
    // the rAF closures see updates without re-renders.
    let skelInitialCount = 0;
    if (spaceWormKind === "skeleton-swarm") {
      // Three small skeleton worms scattered across the upper play
      // field. HP 3 each — they read as fast/fragile compared to the
      // tankier Leviathans (small=6, giant=18).
      const SKEL_COUNT = 3;
      const HP = 3;
      skelInitialCount = SKEL_COUNT;
      for (let i = 0; i < SKEL_COUNT; i++) {
        spaceWorms.push({
          kind: "skeleton",
          x: W * (0.25 + 0.25 * i),
          y: GRID_TOP + 50 + (i % 2) * 35,
          vx: (Math.random() < 0.5 ? -1 : 1) * (1.4 + Math.random() * 0.6),
          vy: 0.25 + Math.random() * 0.35,
          hp: HP,
          maxHp: HP,
          size: 0.5,
          wanderT: Math.random() * Math.PI * 2,
          headPhase: Math.random() * Math.PI * 2, // staggered breath
          hitFlash: 0,
          segments: [],
        });
      }
    } else if (spaceWormKind) {
      const isGiant = spaceWormKind === "giant";
      spaceWorms.push({
        kind: spaceWormKind,
        x: W / 2,
        y: GRID_TOP + 60,
        vx: (Math.random() < 0.5 ? -1 : 1) * (isGiant ? 1.2 : 1.8),
        vy: 0.4 + Math.random() * 0.4,
        // Giant worms are bigger and tankier; small worms die fast.
        hp: isGiant ? 18 : 6,
        maxHp: isGiant ? 18 : 6,
        size: isGiant ? 1.0 : 0.55, // multiplier on the base draw scale
        wanderT: Math.random() * Math.PI * 2,
        headPhase: 0,
        hitFlash: 0,
        // Tail trail of recent positions for the segmented body sway.
        segments: [],
      });
    }
    // RARE WORMHOLE WORM — Endless-only collectible hunt. ~1% chance
    // per wave to spawn a teleporting worm with 3 HP that periodically
    // jumps to a new location through a wormhole portal. Defeating it
    // BEFORE all bricks are cleared awards the ultra-rare Singularity
    // ball skin (claimed via POST /api/endless/wormhole-kill).
    // The worm also occasionally fires a slow plasma bolt at the paddle.
    let rareWorm = null;
    if (endless && endlessWave >= 1) {
      // Seed the RNG with the wave number so the same player playing
      // the same wave twice (e.g. via continue) sees consistent behavior.
      // Real spawn rate ~1% per wave.
      if (Math.random() < 0.01) {
        rareWorm = {
          x: W / 2 + (Math.random() - 0.5) * 200,
          y: GRID_TOP + 80,
          // HP 3 means the pilot needs to hit it ~3 times despite the
          // teleport defense — about 2 teleports between hits.
          hp: 3,
          maxHp: 3,
          // Teleport state machine: idle → fade (0.4s) → portal-out (0.3s)
          //   → relocate → portal-in (0.3s) → fade-in (0.4s) → idle.
          // Total teleport cycle ~1.4s, interval ~4-7s.
          phase: "idle",            // idle | fade-out | port-out | port-in | fade-in
          phaseUntil: gameNow() + 1200, // small grace before first teleport
          nextTeleportAt: gameNow() + 4500 + Math.random() * 2500,
          // Portal swirl angle for the visual effect at the worm position.
          portalT: 0,
          hitFlash: 0,
          // Projectile firing — fires a slow bolt every 4-6s while idle.
          nextFireAt: gameNow() + 5000 + Math.random() * 2000,
          // Track if pilot has dealt damage so the celebration toast
          // only fires once on the actual kill.
          spawnedAt: gameNow(),
        };
      }
    }
    stateRef.current = {
      paddle: { x: W / 2 - BASE_PADDLE_W / 2, w: BASE_PADDLE_W, enlargedUntil: 0 },
      balls: [
        {
          x: W / 2,
          y: PADDLE_Y - BALL_RADIUS - 2,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          trail: [],
          stuck: true,
        },
      ],
      blocks: buildBlocks(),
      powerups: [],
      slowUntil: 0,
      bgOffset: 0,
      particles: [],
      blackHoles: [],
      // Endless-only: moonstone pickups on the field + plasma bolts in flight.
      // Both arrays exist on every state but are only populated when endless.
      moonstones: [],
      plasmaBolts: [],
      // Endless-only floating "+1 ☾" labels spawned at the paddle on every
      // moonstone catch. Drifts up, fades out over 600ms. Cleared by the
      // draw helper as entries expire.
      stoneTicks: [],
      // Worm Swarm — small green worms launched manually via the LAUNCH
      // button. Each entry: { x, y, vx, vy, segments: [{x,y}], target,
      // bornAt, killsLeft }. Travel toward the nearest brick; on contact
      // destroy it (full points), spawn particles, then repath. Self-die
      // after `killsLeft` reaches 0 OR after a max lifetime.
      wormSwarms: [],
      // Space Worm boss(es) — chrome-blue skeletal serpent free-roaming
      // around the brick field on every 10th/20th level. See spawn block
      // above for the cadence. Level-clear gate now requires both bricks
      // and worms to be empty.
      spaceWorms,
      // Endless-only rare Wormhole Worm — teleporting collectible target.
      // See state machine in init block above. Null when not spawned.
      rareWorm,
      // Worm-fired plasma bolts (slow projectiles aimed at paddle). Each:
      // { x, y, vx, vy }. Wormhole-worm only.
      wormBolts: [],
      // Blood-Red Stingray Swarm — short-lived (~10s) translucent manta-
      // ray entities spawned by the `stingray` power-up. Each homes in on
      // the nearest boss target (spaceWorm → rareWorm → tier-4 brick) and
      // deals 2 damage on contact before dissipating. Each entry:
      // { x, y, vx, vy, bornAt, life, dmgCooldownUntil, wingPhase }
      stingrays: [],
      // Blood splatter — screen-overlay splotches spawned by stingray
      // boss-kills. Each entry: { x, y, r, alpha, bornAt, life, drips }.
      // Decays from full opacity → 0 over `life` ms (3.2s). Drawn LAST
      // each frame so it sits on top of every gameplay layer.
      bloodSplats: [],
      // Outcome flag for the rare-worm kill so the kill handler knows
      // whether to claim the trophy at level end (kill before clear) or
      // skip it (worm survived).
      rareWormSlainBeforeClear: false,
      // Skeleton-Swarm bonus tracking. `skelInitialCount` is set on
      // mount (3 on every-5 levels, 0 elsewhere); `skelKilledThisLife`
      // counts kills since the last life loss. When the latter equals
      // the former on a kill, the Bone Coin bonus fires.
      skelInitialCount,
      skelKilledThisLife: 0,
      // Set to true once the bonus has been awarded for this life so a
      // re-spawn (none today, but defensive) can't double-grant it.
      skelBonusGranted: false,
    };
    setBallsLeft(1);
    setActivePowerups([]);
    // Reset endless-only active moonstone effects when the canvas re-mounts
    // a fresh run. The wallet itself is server-loaded (NOT reset here), and
    // wave changes don't reset effects (handled by the metrics guard
    // below). Stone-collected/spent counters reset only on a fresh run.
    if (!endless || !metricsRef.current) {
      stonesCollectedRef.current = 0;
      stonesSpentRef.current = 0;
      setMoonstoneFx({
        phaseUntil: 0,
        dilationUntil: 0,
        plasmaUntil: 0,
        plasmaNextFireAt: 0,
        plasmaFireInterval: 1000,
        plasmaBoltsPerFire: 1,
        shieldArmed: false,
      });
    }
    // In endless mode we want metrics to ACCUMULATE across waves so the
    // run-summary onComplete carries totals (blocks destroyed, max balls,
    // duration since the very first wave) — only initialise the metrics
    // record once per run. For normal mode, every level reset clears them.
    if (!endless || !metricsRef.current) {
      metricsRef.current = {
        startedAt: performance.now(),
        blocksDestroyed: 0,
        powerupsCollected: 0,
        maxBalls: 1,
        bossKilled: false,
        lostLife: false,
      };
      // Lunar streak resets at the start of every fresh run alongside the
      // metrics record so the bonus window doesn't leak across runs.
      stoneStreakRef.current = { timestamps: [], bonusLeft: 0, currentChain: 0, bestChainThisRun: 0 };
    }
  }, [buildBlocks, levelCfg, endless, level, endlessWave]);

  // Music control — pass the level so each sector plays its own theme.
  // Re-runs when the level prop changes so crossing sector boundaries
  // (e.g. level 500 → 501) smoothly swaps in the next sector's bed.
  useEffect(() => {
    if (!muted) startMusic(level);
    return () => stopMusic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  // Wave banner trigger — fires when the endless wave number matches a
  // boss cadence (every 5 = skeleton swarm, every 10 = small Leviathan,
  // every 20 = giant). Self-clears after ~1.8s. Only runs in endless
  // mode so regular-play bosses keep their existing cinematic intro.
  useEffect(() => {
    if (!endless || !endlessWave) {
      setWaveBanner(null);
      return undefined;
    }
    const w = endlessWave;
    let cfg = null;
    if (w > 0 && w % 500 !== 0) {
      if (w % 20 === 0) cfg = { kind: "giant", label: "GIANT LEVIATHAN", color: "#ff8eff" };
      else if (w % 10 === 0) cfg = { kind: "small", label: "SPACE LEVIATHAN", color: "#c95fff" };
      else if (w % 5 === 0) cfg = { kind: "skeleton", label: "SKELETON SWARM", color: "#aef0ff" };
    }
    if (!cfg) {
      setWaveBanner(null);
      return undefined;
    }
    setWaveBanner(cfg);
    const t = setTimeout(() => setWaveBanner(null), 1800);
    return () => clearTimeout(t);
  }, [endless, endlessWave]);

  // Boss cinematic trigger — regular-play Leviathan levels only. Skipped
  // in Endless (its own wave banner covers it) and on /500 sector bosses
  // (those have a dedicated boss banner with HP bar already). Sets a
  // 2.4s title card that auto-clears or can be tap-skipped.
  useEffect(() => {
    bossCinematicRef.current = bossCinematic;
  }, [bossCinematic]);
  useEffect(() => {
    bossDefeatedCinematicRef.current = bossDefeatedCinematic;
  }, [bossDefeatedCinematic]);
  useEffect(() => {
    if (endless) return undefined;
    if (!level || level <= 0) return undefined;
    if (level % 500 === 0) return undefined;
    if (levelCfg.boss) return undefined;
    let kind = null;
    if (level % 20 === 0) kind = "giant";
    else if (level % 10 === 0) kind = "small";
    if (!kind) {
      // Non-boss level — make sure no stale zoom carries over.
      bossZoomStartRef.current = 0;
      return undefined;
    }
    const label = kind === "giant" ? "GIANT LEVIATHAN" : "SPACE LEVIATHAN";
    const cfg = {
      kind,
      label,
      levelName: levelCfg.name || "",
      startedAt: performance.now(),
      durationMs: 2400,
    };
    setBossCinematic(cfg);
    // Start the boss camera-zoom timer so the Anglerfish renders zoomed
    // in during the entrance cinematic (1.06×) and gently pulls back to
    // 1.0× over the next ~2.6s of gameplay. Refs only — no re-renders.
    bossZoomStartRef.current = performance.now();
    // Layered boss roar SFX — fires once on cinematic mount. Tucks
    // inside the 2.4s overlay and naturally cross-fades back into the
    // bgm. Skipped if the user has muted; `Sounds.bossRoar` already
    // gates on the mute flag internally.
    Sounds.bossRoar();
    const t = setTimeout(() => setBossCinematic(null), cfg.durationMs);
    return () => clearTimeout(t);
  }, [level, endless, levelCfg]);
  useEffect(() => {
    if (muted) stopMusic();
    else if (!gameOver && !won) startMusic(level);
  }, [muted, gameOver, won, level]);

  // Boss tension layer — adds a low filtered-noise drone + dissonant
  // minor-second oscillator cluster on top of the music whenever the
  // pilot enters a boss level (sector boss every 500, mini-boss every
  // 10/20). The layer crossfades in over 1.5s, so it lands like a wave
  // rather than a jump-cut. Cleaned up on level change + game end +
  // mute toggle so we never leave the drone running.
  useEffect(() => {
    const isBossLevel = !!(
      level
      && !endless
      && (levelCfg?.boss || level % 10 === 0 || level % 20 === 0)
    );
    if (isBossLevel && !muted && !gameOver && !won) {
      startTensionLayer();
    } else {
      stopTensionLayer();
    }
    return () => stopTensionLayer();
  }, [level, endless, levelCfg, muted, gameOver, won]);

  // (Re)initialise world whenever the level changes.
  // In endless mode, this fires every wave (because levelOverride changes
  // each time the parent bumps `wave`) — but we must NOT reset lives/score
  // between waves; only the blocks/balls/powerups reset (handled by
  // initState). The waveClearedRef guard is also reset so the next wave's
  // "blocks empty" detection re-arms.
  const waveClearedRef = useRef(false);
  const isFirstMountRef = useRef(true);
  useEffect(() => {
    // ENDLESS: snapshot the current blood splatter array BEFORE initState
    // rebuilds the per-wave state. We restore it right after so the
    // splatters from earlier kills persist across wave transitions.
    // Skipped on first mount because there's nothing to preserve yet.
    let preservedSplats = null;
    if (endless && !isFirstMountRef.current && stateRef.current?.bloodSplats) {
      preservedSplats = stateRef.current.bloodSplats;
    }
    initState();
    if (preservedSplats && stateRef.current) {
      stateRef.current.bloodSplats = preservedSplats;
    }
    waveClearedRef.current = false;
    // Re-init guard:
    //   - Endless / Daily / Campaign-resume: only re-init on the very
    //     first mount (later effect re-runs would otherwise clobber
    //     the saved state — also defends against React 18 strict-mode
    //     double-invocation of effects).
    //   - Plain campaign without resume: re-init on every effect run
    //     (level change = restart).
    const hasResumeIntent =
      (endless && typeof endlessStartLives === "number") ||
      (isDaily && typeof dailyStartLives === "number") ||
      (!endless && !isDaily && typeof campaignStartLives === "number");
    const isResumableMode = endless || isDaily || hasResumeIntent;
    const isCampaignResumeFirstMount =
      !endless &&
      !isDaily &&
      isFirstMountRef.current &&
      typeof campaignStartLives === "number";
    if (!isResumableMode || isFirstMountRef.current) {
      let startLives = 3;
      let startScore = 0;
      if (isFirstMountRef.current) {
        if (endless && typeof endlessStartLives === "number") {
          startLives = Math.max(1, Math.min(9, endlessStartLives));
        } else if (isDaily && typeof dailyStartLives === "number") {
          startLives = Math.max(1, Math.min(9, dailyStartLives));
          if (typeof dailyStartScore === "number" && dailyStartScore >= 0) {
            startScore = Math.max(0, Math.min(10_000_000_000, Math.round(dailyStartScore)));
          }
        } else if (isCampaignResumeFirstMount) {
          startLives = Math.max(1, Math.min(9, campaignStartLives));
          if (typeof campaignStartScore === "number" && campaignStartScore >= 0) {
            startScore = Math.max(0, Math.min(10_000_000_000, Math.round(campaignStartScore)));
          }
        }
      }
      setLives(startLives);
      livesRef.current = startLives;
      setScore(startScore);
      setGameOver(false);
      setWon(false);
      setPaused(false);
      isFirstMountRef.current = false;
    }
    // Deferred starter auto-arm.
    //
    // ENDLESS MODE — recurring milestone perk:
    //   wave-5-plasma  → fires on EVERY 5th wave (5, 10, 15, 20…)
    //   wave-10-comet  → fires on EVERY 10th wave (10, 20, 30…)
    // The recurring fire is "boosted" (longer duration + 3-bolt fan
    // for plasma; 16 streaks + 55% destruction for comet) — a true
    // Endless-mode set-piece, not a one-shot starter consumable.
    //
    // Any pre-purchased pending starter of the same kind is also
    // marked as used so the server consumes it (the milestone already
    // fired the effect for them at no extra cost).
    if (endless) {
      const wave = levelCfg.wave || 1;
      const pending = walletCfgRef.current.pendingStarters || [];
      for (const [sid, cfg] of Object.entries(DEFERRED_STARTERS)) {
        const isMilestone = wave > 0 && wave % cfg.wave === 0;
        if (isMilestone) {
          applyDeferredStarterRef.current(sid, { boosted: true });
          // Surface a celebratory toast so the pilot reads "this fired
          // because I hit a milestone" rather than guessing.
          const tier = sid === "wave-5-plasma" ? "PLASMA BOOST" : "COMET STORM";
          const interval = cfg.wave;
          toast.success(`${tier} · WAVE ${wave}`, {
            description: `Auto-armed every ${interval} waves — endless-mode perk.`,
            icon: <Sparkles className="w-4 h-4" style={{ color: cfg.color }} />,
            duration: 2200,
          });
          if (pending.includes(sid) && !startersUsedRef.current.includes(sid)) {
            startersUsedRef.current.push(sid);
          }
        }
      }
    }
  }, [level, initState, endless, levelCfg]);

  // Drive the power-up HUD countdown repaint (and auto-expire stale chips)
  // without coupling to the raf loop — a cheap 150ms interval is plenty for
  // a 3px progress bar. Pauses when the game is paused or ended.
  useEffect(() => {
    if (paused || gameOver || won) return;
    const id = setInterval(() => {
      setHudTick((t) => t + 1);
      setActivePowerups((prev) => {
        const now = performance.now();
        const filtered = prev.filter((p) => p.until > now);
        return filtered.length === prev.length ? prev : filtered;
      });
    }, 150);
    return () => clearInterval(id);
  }, [paused, gameOver, won]);
  const launchStuckBalls = useCallback(() => {
    // Boss cinematic is up — first tap/space/click dismisses the title
    // card without actually launching the ball. Pilots get a beat to
    // read "SPACE LEVIATHAN" before they yeet themselves into its maw.
    if (bossCinematicRef.current) {
      bossCinematicRef.current = null;
      setBossCinematic(null);
      return;
    }
    const s = stateRef.current;
    if (!s) return;
    const speed = levelCfg.ballSpeed;
    s.balls.forEach((b) => {
      if (!b.stuck) return;
      const paddleCenter = s.paddle.x + s.paddle.w / 2;
      const offset = (paddleCenter / W - 0.5) * 0.6;
      const angle = -Math.PI / 2 + offset + (Math.random() - 0.5) * 0.25;
      b.vx = Math.cos(angle) * speed;
      b.vy = Math.sin(angle) * speed;
      b.stuck = false;
    });
  }, [levelCfg]);

  const togglePause = useCallback(() => setPaused((p) => !p), []);

  // Centralised application of a deferred starter's runtime effect.
  // Used in two places:
  //  1) The wave-arm sweep at run/wave start (auto-arm at scheduled wave).
  //  2) The new lower-right power-up tray "Arm Now" tap, which pays 25 ☾
  //     to fire the effect ahead of schedule (see `earlyArmStarter`).
  const applyDeferredStarter = useCallback((sid, opts = {}) => {
    const cfg = DEFERRED_STARTERS[sid];
    if (!cfg) return;
    const tNow = gameNow();
    const boosted = !!opts.boosted;
    // Boosted flashes burn for a hair longer + glow brighter so the
    // milestone fires read as a "the universe just shifted" beat
    // rather than a routine shop activation.
    fxFlashRef.current = { color: cfg.color, until: tNow + (boosted ? 360 : 200) };
    if (sid === "wave-5-plasma") {
      // Plasma Boost: shop default = 8s @ 1 bolt/s vertical.
      // ENDLESS MILESTONE (boosted): 12s @ 2 bolts/s as a 3-bolt fan
      // (left/center/right) — feels like a Vulcan cannon mounted on
      // the paddle. Same plasmaUntil semantics so an early-arm shop
      // purchase that overlaps a milestone window just extends it.
      const duration = boosted ? 12000 : MOONSTONE_POWERUP_DEFS.plasma.duration;
      setMoonstoneFx((fx) => ({
        ...fx,
        plasmaUntil: Math.max(fx.plasmaUntil, tNow + duration),
        plasmaNextFireAt: tNow,
        plasmaFireInterval: boosted ? 500 : 1000,
        plasmaBoltsPerFire: boosted ? 3 : 1,
      }));
      // Boosted milestone fires also get a punchy SFX so the pilot's
      // ear matches the visual cue. The existing fxFlashRef already
      // paints a magenta screen flash; the particle ring around the
      // paddle is deferred to the rAF loop (where spawnParticles is
      // in scope) — see `boostedPlasmaActivationAt` below.
      if (boosted) {
        boostedPlasmaActivationAt.current = tNow;
        Sounds.brick?.();
      }
    } else if (sid === "wave-10-comet") {
      // Comet Storm: shop default = 8 streaks, 30% destruction, 200ms shake.
      // ENDLESS MILESTONE (boosted): 16 streaks (denser shower),
      // 55% destruction, longer 480ms streak life, 360ms shake at
      // magnitude 10 (~1.7x default). Feels like a meteor barrage
      // worth standing back from.
      const numStreaks = boosted ? 16 : 8;
      const streakLife = boosted ? 480 : 400;
      const streaks = [];
      for (let k = 0; k < numStreaks; k++) {
        const angle = (Math.PI / 4) + (Math.random() - 0.5) * (boosted ? 0.5 : 0.3);
        const startX = Math.random() * W;
        const startY = -30 - Math.random() * 120;
        const length = (boosted ? 360 : 280) + Math.random() * 160;
        streaks.push({
          x1: startX,
          y1: startY,
          x2: startX + Math.cos(angle) * length,
          y2: startY + Math.sin(angle) * length,
          t0: tNow + Math.random() * 120,
          life: streakLife,
          // Tag boosted streaks so the renderer can pump glow + thickness.
          boosted,
        });
      }
      cometStormRef.current = {
        streaks,
        destroyAt: tNow + (boosted ? 380 : 320),
        // Stash the destruction ratio on the ref so the raf-loop body
        // (which actually does the destruction at destroyAt) picks the
        // right percentage without an extra closure.
        destroyRatio: boosted ? 0.55 : 0.30,
        boosted,
      };
    }
  }, []);
  const applyDeferredStarterRef = useRef(applyDeferredStarter);
  applyDeferredStarterRef.current = applyDeferredStarter;

  // Early-arm — spend 25 ☾ to immediately fire a pending deferred
  // starter mid-run instead of waiting for its trigger wave. Server is
  // the source of truth: it deducts the cost atomically and removes one
  // matching starter from `pending_starters`. On success we mirror the
  // mutation locally + fire the visual effect via `applyDeferredStarter`.
  const earlyArmStarter = useCallback(async (sid) => {
    const cfg = walletCfgRef.current;
    const pending = cfg.pendingStarters || [];
    if (!pending.includes(sid)) return;
    if (!DEFERRED_STARTERS[sid]) return;
    if (paused || gameOver || won) return;
    if (startersUsedRef.current.includes(sid)) return;
    try {
      const r = await apiClient.post("/moonstones/early-arm", { starter_id: sid });
      const newPending = r?.data?.pending_starters || pending.filter((x) => x !== sid);
      const newBalance = typeof r?.data?.balance === "number" ? r.data.balance : null;
      setWalletCfg((c) => ({
        ...c,
        pendingStarters: newPending,
        ...(newBalance !== null ? { balance: newBalance } : {}),
      }));
      // Keep the canonical `moonstones` wallet state in sync so the
      // ☾ HUD and PowerupTray.balance prop both reflect the deduction.
      if (newBalance !== null) setMoonstones(newBalance);
      startersUsedRef.current.push(sid);
      applyDeferredStarterRef.current(sid);
    } catch (e) {
      // Server rejected (insufficient balance, not pending, etc.) — surface
      // a quick toast so the pilot knows the spend didn't go through.
      const msg = e?.response?.data?.detail || "Couldn't arm — try again.";
      toast.error(msg);
    }
  }, [paused, gameOver, won]);

  // Worm Swarm — manually launches a flock of 5 tiny green worms from the
  // paddle. Available in both regular play and Endless when the pilot has
  // a `worm-swarm` starter pending. Pops one starter from the wallet via
  // the new /moonstones/consume-starter endpoint. Optimistic local update
  // so the LAUNCH button reflects the new pending count immediately.
  const launchWormSwarm = useCallback(() => {
    const cfg = walletCfgRef.current;
    const pending = cfg.pendingStarters || [];
    if (!pending.includes("worm-swarm")) return;
    if (gameOver || won) return;
    // Pop one local pending starter.
    const newPending = [...pending];
    newPending.splice(newPending.indexOf("worm-swarm"), 1);
    setWalletCfg((c) => ({ ...c, pendingStarters: newPending }));
    apiClient.post("/moonstones/consume-starter", { starter_id: "worm-swarm" }).catch(() => {});

    // Spawn 5 worms at the paddle, scattered ±90° upward fan.
    const s = stateRef.current;
    if (!s) return;
    s.wormSwarms = s.wormSwarms || [];
    const count = 5;
    const px = s.paddle.x + s.paddle.w / 2;
    const py = PADDLE_Y - 8;
    const nowTs = performance.now();
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (i - (count - 1) / 2) * 0.45 + (Math.random() - 0.5) * 0.2;
      const speed = 3.0;
      s.wormSwarms.push({
        x: px,
        y: py,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        segments: [{ x: px, y: py }],
        targetIdx: -1,
        killsLeft: 3,        // each worm dies after 3 brick hits
        expiresAt: nowTs + 6000, // OR after 6 seconds
        seed: Math.random() * 100,
      });
    }
    Sounds.powerUp();
  }, [gameOver, won]);

  // Locked-skin trial preview — when a pilot taps a locked moonstone skin
  // in the pause switcher, we temporarily render the field stones in that
  // palette for ~5s so they can SEE what the skin looks like before buying.
  // Stored as state for the pause-modal banner; mirrored to a ref so the
  // rAF loop can read the override palette without re-running the effect.
  const [previewSkin, setPreviewSkin] = useState(null); // { id, name, palette, cost, until }
  const previewSkinRef = useRef(null);
  useEffect(() => { previewSkinRef.current = previewSkin; }, [previewSkin]);
  // Helper — returns the palette to render stones with right now. Preview
  // wins over the equipped skin while it's active; expires on its own.
  const getRenderPalette = useCallback(() => {
    const p = previewSkinRef.current;
    if (p && p.until > performance.now()) return p.palette;
    return walletCfgRef.current.skinPalette;
  }, []);

  // Pause-menu skin swap — equip an owned moonstone skin without leaving the
  // run. Optimistic local update so the in-canvas stones recolour instantly;
  // the server call is fire-and-forget. No-op if the skin is already active
  // or not owned.
  const swapSkin = useCallback((skinId) => {
    const cfg = walletCfgRef.current;
    if (!skinId || skinId === cfg.activeSkin) return;
    const owned = cfg.ownedSkins || [];
    if (!owned.includes(skinId)) return;
    const skinDoc = (cfg.skinsCatalog || []).find((s) => s.id === skinId);
    if (!skinDoc) return;
    setWalletCfg((c) => ({
      ...c,
      activeSkin: skinId,
      skinPalette: skinDoc.palette || null,
      skinName: skinDoc.name || skinId,
    }));
    apiClient.post("/moonstones/skin/active", { skin_id: skinId }).catch(() => {});
    Sounds.powerUp();
  }, []);

  // Try a LOCKED skin — purely visual 5-second preview of the field stones
  // in that palette. Does NOT touch the equipped skin or the server. Auto
  // expires; replacing an in-flight preview restarts the timer so a pilot
  // tapping multiple locked chips always sees the latest one.
  const previewTimerRef = useRef(0);
  const tryLockedSkin = useCallback((skinId) => {
    const cfg = walletCfgRef.current;
    const skinDoc = (cfg.skinsCatalog || []).find((s) => s.id === skinId);
    if (!skinDoc) return;
    if ((cfg.ownedSkins || []).includes(skinId)) return; // ignore — owned skins use swapSkin
    const dur = 5000;
    const until = performance.now() + dur;
    setPreviewSkin({
      id: skinId,
      name: skinDoc.name || skinId,
      palette: skinDoc.palette || null,
      cost: Number(skinDoc.cost || 0),
      until,
    });
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      setPreviewSkin(null);
      previewTimerRef.current = 0;
      // Soft conversion nudge — only fires if the pilot doesn't already
      // own the skin by the time the preview expires (they may have
      // bought it via another tab). Click sends them to /skins where
      // the moonstone-store tab is the natural landing.
      const cur = walletCfgRef.current;
      if (!(cur.ownedSkins || []).includes(skinId)) {
        toast(`Liked ${skinDoc.name}?`, {
          description: `Grab it for ${skinDoc.cost} ☾ on /skins.`,
          action: {
            label: "Open Store",
            onClick: () => navigate("/skins"),
          },
          duration: 6000,
          // sonner's `id` dedupes — if the pilot retries the same skin
          // a few times we want only the most recent toast on screen.
          id: `try-${skinId}`,
        });
      }
    }, dur);
    Sounds.powerUp();
  }, [navigate]);
  useEffect(() => () => {
    // Cleanup any pending preview timer on unmount.
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
  }, []);

  // Endless mode shop — spend moonstones from the in-run wallet on premium
  // power-ups. Each purchase deducts cost and arms the matching effect.
  // Star Shield is a one-shot save (no timer); the others have durations
  // pulled from MOONSTONE_SHOP. Plasma uses `plasmaNextFireAt` to stagger
  // the auto-fire cadence. Frugal Pilot perk applies a -1 cost discount.
  const buyMoonstoneItem = useCallback((id) => {
    const item = MOONSTONE_SHOP[id];
    if (!item) return;
    const perks = walletCfgRef.current.perks || [];
    const discount = perks.includes("frugal-pilot") ? 1 : 0;
    const cost = Math.max(1, item.cost - discount);
    if (moonstones < cost) return;
    setMoonstones((c) => c - cost);
    stonesSpentRef.current += cost;
    // Persist the spend immediately so a mid-run browser close can't give
    // the pilot a "free" power-up with no wallet deduction.
    apiClient.post("/moonstones/adjust", { delta: -cost }).catch(() => {});
    const now = gameNow();
    // Activation flash — pulse the canvas for 200ms in the power-up's
    // signature colour. Reads as instant tactile feedback the moment a
    // shop button is clicked, before the slower ambient tint kicks in.
    fxFlashRef.current = { color: item.color, until: now + 200 };
    setMoonstoneFx((fx) => {
      if (id === "phase") return { ...fx, phaseUntil: Math.max(fx.phaseUntil, now + item.duration) };
      if (id === "dilation") return { ...fx, dilationUntil: Math.max(fx.dilationUntil, now + item.duration) };
      if (id === "plasma") return { ...fx, plasmaUntil: Math.max(fx.plasmaUntil, now + item.duration), plasmaNextFireAt: now };
      if (id === "shield") return { ...fx, shieldArmed: true };
      return fx;
    });
    Sounds.powerUp();
    setShopFlashId(id);
    setTimeout(() => setShopFlashId(null), 600);
  }, [moonstones]);

  // Fullscreen-gated hotkey handlers. `restart` is defined further
  // down the component so we can't reference it directly here — the
  // hotkey callbacks instead read from refs that we update after all
  // handlers resolve. `isFullscreen` is returned from `useGameInput`,
  // so it's gated through `isFullscreenRef` for the same reason.
  const isFullscreenRef = useRef(false);
  const hotkeyGameOverRef = useRef(false);
  const hotkeyWonRef = useRef(false);
  const restartFnRef = useRef(() => {});
  const exitFnRef = useRef(() => {});
  const { isFullscreen, pointerLocked, toggleFullscreen, canvasHandlers } = useGameInput({
    canvasRef,
    wrapperRef,
    fsTargetRef,
    keysRef,
    mouseXRef,
    onLaunch: launchStuckBalls,
    onTogglePause: togglePause,
    // Hotkeys for the fullscreen right-side control column: R restarts,
    // K opens the pause panel (where the skin switcher lives), Q quits.
    // Each is gated on isFullscreen so they never interfere with the
    // non-fullscreen flow where the bottom toolbar already provides them.
    onRestart: useCallback(() => {
      if (isFullscreenRef.current) restartFnRef.current();
    }, []),
    onOpenSkins: useCallback(() => {
      if (
        isFullscreenRef.current
        && !hotkeyGameOverRef.current
        && !hotkeyWonRef.current
      ) {
        setPaused(true);
      }
    }, []),
    onQuit: useCallback(() => {
      if (isFullscreenRef.current) exitFnRef.current();
    }, []),
    stopLoopOn: { gameOver, won },
  });
  useEffect(() => {
    isFullscreenRef.current = isFullscreen;
  }, [isFullscreen]);

  // Landscape auto-fullscreen for phones — when the pilot rotates to
  // landscape we enter fullscreen automatically so the canvas fills the
  // whole screen (arcade-cabinet feel). Only fires on touch devices to
  // avoid surprising desktop users who resize their browser window. If
  // the pilot manually exits FS while in landscape we set
  // userExitedLandscapeFsRef so we don't immediately re-enter — they
  // need to rotate to portrait + back to landscape to re-trigger.
  const userExitedLandscapeFsRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const isTouch =
      "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
    if (!isTouch) return undefined;
    const mql = window.matchMedia("(orientation: landscape)");
    const onChange = (e) => {
      if (e.matches) {
        // landscape — auto-enter FS unless the pilot just left it.
        if (!isFullscreenRef.current && !userExitedLandscapeFsRef.current) {
          try {
            toggleFullscreen();
          } catch {
            /* ignore — best-effort */
          }
        }
      } else {
        // portrait — clear the "user manually exited" gate so the next
        // rotation to landscape can auto-FS again.
        userExitedLandscapeFsRef.current = false;
      }
    };
    // Use addEventListener (Safari < 14 fallback to addListener).
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [toggleFullscreen]);

  // Track manual FS exits while landscape so the auto-FS effect above
  // doesn't immediately re-trigger. Cleared the next time the pilot
  // rotates back to portrait.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    if (isFullscreen) return;
    // FS turned OFF — if we're still in landscape on a touch device,
    // record that the pilot opted out so we don't fight them.
    const isTouch = "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
    const landscape = window.matchMedia("(orientation: landscape)").matches;
    if (isTouch && landscape) {
      userExitedLandscapeFsRef.current = true;
    }
  }, [isFullscreen]);

  // Auto-dismiss for the first-moonstone tip. Same 5s window + persist
  // pattern as the hotkey tip so pilots don't get two competing banners
  // on screen. The trigger (setShowMoonstoneTip(true)) only fires once
  // per pilot — this effect resets + persists on hide.
  useEffect(() => {
    if (!showMoonstoneTip) return undefined;
    const t = setTimeout(() => {
      setShowMoonstoneTip(false);
      try {
        localStorage.setItem("cosmic_moonstone_tip_seen", "1");
      } catch {
        // ignore
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [showMoonstoneTip]);

  // First-fullscreen hotkey tip — shown once per pilot. Respects wider
  // viewports only (md+) since that's where the right-side hotkey badges
  // are visible. Hidden once dismissed or after 5s whichever comes first.
  useEffect(() => {
    if (!isFullscreen) return undefined;
    try {
      if (localStorage.getItem("cosmic_hotkey_tip_seen") === "1") return undefined;
      if (typeof window !== "undefined" && window.innerWidth < 768) return undefined;
    } catch {
      return undefined;
    }
    setShowHotkeyTip(true);
    const t = setTimeout(() => {
      setShowHotkeyTip(false);
      try {
        localStorage.setItem("cosmic_hotkey_tip_seen", "1");
      } catch {
        // ignore quota / privacy-mode failures
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [isFullscreen]);
  useEffect(() => {
    hotkeyGameOverRef.current = gameOver;
    hotkeyWonRef.current = won;
  }, [gameOver, won]);

  // HD canvas scaling — boosts the backing buffer to match the actual
  // displayed size × devicePixelRatio so the boss artwork, particles,
  // and bricks stay crisp on retina/4K screens. Drawing code keeps
  // using logical W × H units; we install a scale transform once and
  // re-apply it whenever the canvas resizes.
  //
  // PERF NOTE (2026-05-03 lag fix): The previous version forced a
  // 2×–3× supersample on every device, which at DPR=1 blew the backing
  // buffer to 4× the pixels (1600×1200 vs 800×600), and at DPR=2 to
  // up to 9× (2400×1800 effective). Combined with the dozens of
  // per-frame gradient/shadow operations in the Anglerfish boss draw,
  // mid-tier integrated GPUs tanked to 15–25 fps. New policy:
  //   - DPR ≤ 1     → 1.5× supersample (crispness with minimal cost)
  //   - DPR  > 1    → use the device DPR up to a hard 2× cap
  // This typically halves the pixel fill rate while still producing
  // sharper output than the legacy 1× canvas.
  // Adaptive supersample scale — starts at the per-DPR default and is
  // forcibly downgraded by the FPS watchdog (further down) when the
  // game can't sustain 50 fps. Stored in a ref so the watchdog can
  // mutate it without re-running the layout effect; an explicit
  // `lowPerfMode` state flag triggers re-renders for any UI hooked to
  // the perf state (e.g. a future "low-perf mode active" toast).
  const supersampleRef = useRef(0);
  const [lowPerfMode, setLowPerfMode] = useState(false);  // eslint-disable-line no-unused-vars

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const apply = (overrideScale) => {
      const baseDpr = window.devicePixelRatio || 1;
      const defaultScale = baseDpr <= 1 ? 1.5 : Math.min(baseDpr, 2);
      const dpr = overrideScale || defaultScale;
      supersampleRef.current = dpr;
      const rect = canvas.getBoundingClientRect();
      const cssW = rect.width || W;
      const cssH = rect.height || H;
      const targetW = Math.max(1, Math.round(cssW * dpr));
      const targetH = Math.max(1, Math.round(cssH * dpr));
      if (canvas.width !== targetW) canvas.width = targetW;
      if (canvas.height !== targetH) canvas.height = targetH;
      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.setTransform(targetW / W, 0, 0, targetH / H, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "low";
    };
    apply();
    // Expose the apply fn on the canvas DOM node so the FPS watchdog
    // can request a downgrade without setting up a complex ref-pyramid.
    canvas.__hdApply = apply;
    const ro = new ResizeObserver(() => apply());
    ro.observe(canvas);
    window.addEventListener("resize", () => apply());
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", () => apply());
      delete canvas.__hdApply;
    };
  }, []);

  // Adaptive performance watchdog — measures rolling frame time over a
  // 1.5s window. If the average frame time exceeds 22ms (~45fps) for two
  // consecutive windows, drop the supersample to 1.0× (native pixels).
  // One-shot: once we're in low-perf mode, we don't try to climb back
  // up — the fluctuation would feel worse than a steady lower quality.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let raf = 0;
    let prevT = performance.now();
    let frameSum = 0;
    let frameCount = 0;
    let slowWindows = 0;
    let degraded = false;
    const tick = () => {
      const now = performance.now();
      const dt = now - prevT;
      prevT = now;
      // Ignore initial tab-switch / hidden frames (huge dt spikes).
      if (dt < 200) {
        frameSum += dt;
        frameCount += 1;
      }
      if (frameCount >= 90) {  // ~1.5s @ 60fps
        const avg = frameSum / frameCount;
        if (avg > 22) {
          slowWindows += 1;
          if (slowWindows >= 2 && !degraded) {
            degraded = true;
            setLowPerfMode(true);
            if (typeof canvas.__hdApply === "function") {
              canvas.__hdApply(1.0);  // drop to native res
            }
          }
        } else {
          slowWindows = 0;
        }
        frameSum = 0;
        frameCount = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Main game loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const spawnPowerup = (x, y) => {
      if (Math.random() > POWERUP_CHANCE) return;
      stateRef.current.powerups.push({ x, y, kind: pickWeightedPowerup(), vy: 1.8 });
    };

    // Combo helpers — closure-bound to the rAF loop so they can read/write
    // comboRef without going through React state.
    const bumpCombo = (n = 1) => {
      const now = performance.now();
      const c = comboRef.current;
      const before = now - c.lastAt > COMBO_WINDOW_MS ? 0 : c.count;
      if (now - c.lastAt > COMBO_WINDOW_MS) {
        c.count = 0;
      }
      c.count += n;
      c.lastAt = now;
      if (c.count > c.runBest) c.runBest = c.count;
      if (metricsRef.current && c.count > (metricsRef.current.bestCombo || 0)) {
        metricsRef.current.bestCombo = c.count;
      }
      setComboCount(c.count);
      // Haptic combo cue — fires when crossing the multiplier thresholds
      // (3, 5, 10, 25 — same brackets used by comboMultiplier above).
      // No-op on devices without the Vibration API.
      const COMBO_THRESHOLDS = [3, 5, 10, 25];
      if (COMBO_THRESHOLDS.some((t) => before < t && c.count >= t)) {
        Haptic.combo();
      }
    };
    const resetCombo = () => {
      const c = comboRef.current;
      if (c.count !== 0) {
        c.count = 0;
        setComboCount(0);
      }
    };
    // Score multiplier from current combo. >=3 → 1.5×, >=5 → 2×, >=10 → 3×,
    // >=25 → 5×. Applied to brick-destroy points; chip score (10 per hit)
    // stays raw so partial-hit chip damage doesn't inflate the multiplier.
    const comboMultiplier = (count) => {
      if (count >= 25) return 5;
      if (count >= 10) return 3;
      if (count >= 5) return 2;
      if (count >= 3) return 1.5;
      return 1;
    };

    // Endless-only moonstone drop. Rolled per destroyed block; the stone
    // falls toward the paddle and is collected when the paddle catches it.
    // Drop rate is intentionally generous (12%, +6% with the Stone Hunter
    // perk) so a typical wave nets 5-8 stones.
    const spawnMoonstone = (x, y) => {
      if (!endless) return;
      const perks = walletCfgRef.current.perks || [];
      const bonus = perks.includes("stone-hunter") ? 0.06 : 0;
      if (Math.random() > MOONSTONE_DROP_CHANCE + bonus) return;
      stateRef.current.moonstones.push({
        x,
        y,
        vy: MOONSTONE_FALL_SPEED,
        r: MOONSTONE_RADIUS,
        seed: Math.random() * Math.PI * 2,
      });
    };

    const spawnParticles = (x, y, color) => {
      const s = stateRef.current;
      for (let i = 0; i < 8; i++) {
        s.particles.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 4,
          vy: (Math.random() - 0.5) * 4,
          life: 1,
          color,
        });
      }
    };

    /**
     * spawnBloodSplats — paints a screen-wide blood splatter overlay
     * spawned from a kill at (originX, originY). Used by the stingray
     * boss-kill path. Creates 16-22 crimson splotches scattered across
     * the canvas with random radii + a few short "drip" tails. Each
     * splat decays from full alpha → 0 over ~3.2s.
     */
    const spawnBloodSplats = (originX, originY) => {
      const s = stateRef.current;
      const now = performance.now();
      const count = 16 + Math.floor(Math.random() * 7);
      // ENDLESS MODE: splats persist for the entire run as a "trophy
      // wall" of carnage. Use a multi-minute life so they outlive any
      // realistic wave transition. They still slow-fade over their
      // lifetime so the screen doesn't become unreadable on hour-long
      // runs.
      const centralLife = endless ? 240000 : 3500;
      const splatLife = endless ? 180000 : 2400;
      const splatLifeJitter = endless ? 30000 : 1200;
      // One big central splat at the kill site.
      s.bloodSplats.push({
        x: originX,
        y: originY,
        r: 46 + Math.random() * 14,
        rot: Math.random() * Math.PI * 2,
        bornAt: now,
        life: centralLife,
        drips: 4 + Math.floor(Math.random() * 3),
      });
      // Scattered splats — biased toward kill site, but spread across
      // the whole canvas so it reads as a "screen splatter".
      for (let i = 0; i < count; i++) {
        // 60% of splats land within a 280px radius of the kill site,
        // 40% land anywhere on screen for that "spatter" look.
        let sx, sy;
        if (Math.random() < 0.6) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.random() * 280;
          sx = originX + Math.cos(a) * d;
          sy = originY + Math.sin(a) * d;
        } else {
          sx = Math.random() * W;
          sy = GRID_TOP + Math.random() * (H - GRID_TOP - 60);
        }
        s.bloodSplats.push({
          x: Math.max(8, Math.min(W - 8, sx)),
          y: Math.max(GRID_TOP, Math.min(H - 20, sy)),
          r: 6 + Math.random() * 24,
          rot: Math.random() * Math.PI * 2,
          bornAt: now,
          life: splatLife + Math.random() * splatLifeJitter,
          drips: Math.random() < 0.4 ? 1 + Math.floor(Math.random() * 3) : 0,
        });
      }
      // Cap the array so a back-to-back triple kill can't blow up the
      // canvas with thousands of splotches. Higher cap in endless since
      // it persists across waves.
      const cap = endless ? 480 : 220;
      if (s.bloodSplats.length > cap) s.bloodSplats.splice(0, s.bloodSplats.length - cap);
      Sounds.destroyBlock?.();
    };

    const applyPowerup = (kind) => {
      const s = stateRef.current;
      const now = performance.now();
      // Subtle haptic thump on every power-up pickup. No-op on devices
      // without the Vibration API (desktop, iOS Safari).
      Haptic.tap();
      if (kind === "multi") {
        const existing = s.balls.filter((b) => !b.stuck);
        const newBalls = [];
        existing.slice(0, 3).forEach((b) => {
          for (let k = 0; k < 2; k++) {
            const spd = Math.hypot(b.vx, b.vy) || levelCfg.ballSpeed;
            const a = Math.atan2(b.vy, b.vx) + (k === 0 ? -0.5 : 0.5);
            newBalls.push({
              x: b.x,
              y: b.y,
              vx: Math.cos(a) * spd,
              vy: Math.sin(a) * spd,
              trail: [],
              stuck: false,
            });
          }
        });
        s.balls.push(...newBalls);
        Sounds.powerUp();
      } else if (kind === "enlarge") {
        s.paddle.w = BASE_PADDLE_W * 1.6;
        s.paddle.enlargedUntil = now + 12000;
        Sounds.powerUp();
      } else if (kind === "slow") {
        s.slowUntil = now + 8000;
        Sounds.powerUp();
      } else if (kind === "life") {
        setLives((l) => l + 1);
        Sounds.extraLife();
      } else if (kind === "warp") {
        // Teleport every in-flight ball to a random spot in the upper half of
        // the play area. Trail is cleared so the comet tail doesn't streak
        // from the old spot to the new one.
        const candidates = s.balls.filter((b) => !b.stuck);
        candidates.forEach((b) => {
          spawnParticles(b.x, b.y, "#b026ff");
          const margin = 60;
          b.x = margin + Math.random() * (W - margin * 2);
          b.y = GRID_TOP + Math.random() * (H * 0.35);
          b.trail = [];
          spawnParticles(b.x, b.y, "#d462ff");
          // Preserve speed, randomise angle (but bias downward so the game
          // doesn't stall with the ball stuck floating upward forever).
          const spd = Math.hypot(b.vx, b.vy) || levelCfg.ballSpeed;
          const angle = Math.PI * (0.25 + Math.random() * 0.5); // 45°–135°
          b.vx = Math.cos(angle) * spd;
          b.vy = Math.sin(angle) * spd;
        });
        Sounds.warp();
      } else if (kind === "hole") {
        // Spawn a short-lived black hole centred on a live block if one is
        // available, otherwise on the paddle. Any block whose centre lies
        // within the blast radius is destroyed, awarding score + spawning
        // particles (no powerup drop — chain-reacting blackholes would be
        // broken). The VFX lasts ~900ms.
        const candidates = s.blocks;
        const target = candidates.length
          ? candidates[Math.floor(Math.random() * candidates.length)]
          : { x: s.paddle.x + s.paddle.w / 2 - 40, y: H / 2, w: 80, h: 28 };
        const cx = target.x + target.w / 2;
        const cy = target.y + target.h / 2;
        const maxR = 120;
        s.blackHoles.push({
          x: cx,
          y: cy,
          startedAt: now,
          duration: 900,
          maxRadius: maxR,
        });
        let destroyed = 0;
        for (let i = s.blocks.length - 1; i >= 0; i--) {
          const b = s.blocks[i];
          const bx = b.x + b.w / 2;
          const by = b.y + b.h / 2;
          if (Math.hypot(bx - cx, by - cy) <= maxR) {
            const color = TIER_COLORS[b.tier];
            spawnParticles(bx, by, color);
            s.blocks.splice(i, 1);
            destroyed += 1;
          }
        }
        if (destroyed > 0) {
          bumpCombo(destroyed);
          const mult = comboMultiplier(comboRef.current.count);
          setScore((sc) => sc + Math.round(100 * destroyed * mult));
          if (metricsRef.current) {
            metricsRef.current.blocksDestroyed += destroyed;
          }
        }
        Sounds.blackhole();
      } else if (kind === "stingray") {
        // Spawn 3 small translucent blood-red stingrays just above the
        // paddle. Each homes in on the nearest boss target (spaceWorm
        // → rareWorm → tier-4 brick) and deals 2 damage on contact
        // (with a 250ms self-cooldown per stingray so a single rapid
        // brush doesn't shred a Leviathan in one frame). 10s lifetime
        // — they dissipate even if no kill is scored.
        const px = s.paddle.x + s.paddle.w / 2;
        const py = PADDLE_Y - 24;
        const wingPhases = [0, Math.PI * 2 / 3, Math.PI * 4 / 3];
        for (let k = 0; k < 3; k++) {
          const a = -Math.PI / 2 + (k - 1) * 0.45; // upward fan
          const sp = 4.2;
          s.stingrays.push({
            x: px + (k - 1) * 18,
            y: py,
            vx: Math.cos(a) * sp,
            vy: Math.sin(a) * sp,
            bornAt: now,
            life: 10000,
            dmgCooldownUntil: 0,
            wingPhase: wingPhases[k],
          });
          spawnParticles(px + (k - 1) * 18, py, "#ff1f3a");
        }
        Sounds.powerUp();
      }
      setActivePowerups((prev) => {
        const info = POWERUP_INFO[kind] || { duration: 12000 };
        const entry = { kind, startedAt: now, until: now + info.duration, duration: info.duration };
        return [...prev, entry].slice(-5);
      });
      // First-time-this-session pickup → onboard the user with a toast.
      const seen = seenPowerupsRef.current;
      if (!seen.has(kind)) {
        seen.add(kind);
        const info = POWERUP_INFO[kind];
        if (info) {
          toast.success(info.label, {
            description: info.desc,
            icon: <Sparkles className="w-4 h-4" style={{ color: info.color }} />,
            duration: 2400,
          });
        }
      }
    };

    const drawFrame = (s) => {
      s.blocks.forEach((b) => drawBlock(ctx, b));
      s.powerups.forEach((p) => drawPowerup(ctx, p));
      // Bomb+Chain synergy — lightning arcs from the bomb's radius
      // hits to their outside neighbors. Each arc is a jagged 3-segment
      // polyline with a cyan glow halo + white inner core. Self-prunes
      // entries past their TTL.
      if (bombArcsRef.current.length) {
        const now = performance.now();
        const live = [];
        ctx.save();
        for (const a of bombArcsRef.current) {
          const age = now - a.bornAt;
          if (age >= a.ttlMs) continue;
          live.push(a);
          const t = age / a.ttlMs;          // 0 → 1
          const alpha = 1 - t;
          const dx = a.x2 - a.x1;
          const dy = a.y2 - a.y1;
          // 3-segment jitter polyline so the arc reads as electric.
          const jx1 = a.x1 + dx * 0.33 + (Math.random() - 0.5) * 14;
          const jy1 = a.y1 + dy * 0.33 + (Math.random() - 0.5) * 14;
          const jx2 = a.x1 + dx * 0.66 + (Math.random() - 0.5) * 14;
          const jy2 = a.y1 + dy * 0.66 + (Math.random() - 0.5) * 14;
          ctx.globalAlpha = alpha;
          ctx.shadowBlur = 14;
          ctx.shadowColor = "#67e8f9";
          ctx.strokeStyle = "#67e8f9";
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(a.x1, a.y1);
          ctx.lineTo(jx1, jy1);
          ctx.lineTo(jx2, jy2);
          ctx.lineTo(a.x2, a.y2);
          ctx.stroke();
          // White-hot core overlaid for the lightning brightness.
          ctx.shadowBlur = 0;
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(a.x1, a.y1);
          ctx.lineTo(jx1, jy1);
          ctx.lineTo(jx2, jy2);
          ctx.lineTo(a.x2, a.y2);
          ctx.stroke();
        }
        ctx.restore();
        bombArcsRef.current = live;
      }
      // Endless: opal moonstones drift down between the brick field and
      // the paddle so they read clearly against the deep cosmic backdrop.
      if (s.moonstones && s.moonstones.length) {
        const palette = getRenderPalette();
        s.moonstones.forEach((m) => drawMoonstone(ctx, m, palette));
      }
      if (s.plasmaBolts && s.plasmaBolts.length) {
        s.plasmaBolts.forEach((p) => drawPlasmaBolt(ctx, p));
      }
      // Laser Paddle artifact bolts — slim cyan beams with a magenta
      // core, two pixels wide, drawn with a glow halo.
      if (s.laserBolts && s.laserBolts.length) {
        ctx.save();
        for (const p of s.laserBolts) {
          ctx.shadowBlur = 14;
          ctx.shadowColor = "#7df9ff";
          ctx.strokeStyle = "#7df9ff";
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y - 14);
          ctx.lineTo(p.x, p.y + 6);
          ctx.stroke();
          // Inner magenta core
          ctx.shadowBlur = 0;
          ctx.strokeStyle = "#ff3df0";
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y - 12);
          ctx.lineTo(p.x, p.y + 4);
          ctx.stroke();
        }
        ctx.restore();
      }
      // Space Worm bosses — chrome-blue skeletal serpents free-roaming
      // around the field on every-10/every-20 levels. Drawn after bricks
      // so the worm passes ABOVE the brick field; below the paddle (drawn
      // next) so the paddle never gets visually obscured.
      if (s.spaceWorms && s.spaceWorms.length) {
        s.spaceWorms.forEach((w) => drawSpaceWorm(ctx, w));
      }
      // Blood-Red Stingrays — translucent manta-ray silhouettes spawned
      // by the `stingray` power-up. Drawn after space worms so they
      // visually pass IN FRONT of the bosses they're hunting. Pure
      // canvas drawing — no sprite sheet — keeps the asset list zero.
      if (s.stingrays && s.stingrays.length) {
        ctx.save();
        for (const r of s.stingrays) {
          const ang = Math.atan2(r.vy, r.vx);
          const flap = Math.sin(r.wingPhase) * 0.35; // wing oscillation
          // Fade-in over first 250ms, fade-out over last 600ms.
          const age = performance.now() - r.bornAt;
          const remaining = r.life - age;
          const alpha = Math.min(
            1,
            Math.min(age / 250, remaining / 600)
          ) * 0.62; // overall translucency cap
          ctx.save();
          ctx.translate(r.x, r.y);
          ctx.rotate(ang);
          ctx.globalAlpha = Math.max(0, alpha);
          // Drop shadow / glow
          ctx.shadowBlur = 14;
          ctx.shadowColor = "#ff1f3a";
          // Body (diamond-ish manta shape).
          const wingSpan = 18;
          const wingTip = 11 + flap * 4;
          ctx.fillStyle = "#b3001f";
          ctx.beginPath();
          ctx.moveTo(10, 0);                 // nose
          ctx.quadraticCurveTo(2, -wingTip, -wingSpan, -3); // left wing
          ctx.quadraticCurveTo(-4, 0, -wingSpan, 3);        // tail-side cusp
          ctx.quadraticCurveTo(2, wingTip, 10, 0);          // right wing → nose
          ctx.closePath();
          ctx.fill();
          // Inner highlight stripe (lighter red).
          ctx.shadowBlur = 0;
          ctx.fillStyle = "#ff3a52";
          ctx.beginPath();
          ctx.moveTo(7, 0);
          ctx.quadraticCurveTo(0, -wingTip * 0.55, -10, -1.5);
          ctx.quadraticCurveTo(-2, 0, -10, 1.5);
          ctx.quadraticCurveTo(0, wingTip * 0.55, 7, 0);
          ctx.closePath();
          ctx.fill();
          // Whip tail.
          ctx.strokeStyle = "#ff1f3a";
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.moveTo(-wingSpan + 2, 0);
          ctx.quadraticCurveTo(-wingSpan - 6, flap * 2, -wingSpan - 14, flap * 4);
          ctx.stroke();
          ctx.restore();
        }
        ctx.restore();
      }
      // Rare Wormhole Worm — Endless-only collectible. A pulsing
      // violet/black orb with a swirling event-horizon ring. Renders
      // with phase-dependent alpha so it appears to teleport through
      // a portal: idle = 1.0, fade-out → 0.0, port-out = 0.0,
      // port-in = 0.0, fade-in → 1.0. The portal ring scales with
      // phase too — large + spinning during teleport, small + still
      // when idle. Plus an HP-pip cluster above the body.
      if (s.rareWorm) {
        const rw = s.rareWorm;
        const now = gameNow();
        // Alpha + scale per phase.
        let alpha = 1;
        let portalScale = 0.6;
        if (rw.phase === "fade-out") {
          const t = Math.max(0, Math.min(1, (rw.phaseUntil - now) / 400));
          alpha = t;
          portalScale = 0.6 + (1 - t) * 1.6;
        } else if (rw.phase === "port-out" || rw.phase === "port-in") {
          alpha = 0;
          portalScale = 2.2;
        } else if (rw.phase === "fade-in") {
          const t = 1 - Math.max(0, Math.min(1, (rw.phaseUntil - now) / 400));
          alpha = t;
          portalScale = 0.6 + (1 - t) * 1.6;
        }
        const cx = rw.x;
        const cy = rw.y;
        const baseR = 22;
        // Wormhole portal ring — spinning radial gradient.
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rw.portalT);
        const ringR = baseR * portalScale * 1.6;
        const grad = ctx.createRadialGradient(0, 0, baseR * 0.4, 0, 0, ringR);
        grad.addColorStop(0, "rgba(255,0,255,0.0)");
        grad.addColorStop(0.55, "rgba(255,0,255,0.55)");
        grad.addColorStop(0.85, "rgba(58,10,102,0.8)");
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(0, 0, ringR, 0, Math.PI * 2);
        ctx.fill();
        // Crackling tangential strokes for the event-horizon shimmer.
        const arms = 6;
        for (let a = 0; a < arms; a++) {
          ctx.strokeStyle = a % 2 === 0
            ? `rgba(255,0,255,${0.65 * portalScale * 0.5})`
            : `rgba(0,255,255,${0.5 * portalScale * 0.5})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          const ang0 = (a / arms) * Math.PI * 2;
          ctx.arc(0, 0, ringR * 0.85, ang0, ang0 + 0.7);
          ctx.stroke();
        }
        ctx.restore();
        // Body — pulsing dark violet orb with magenta core.
        if (alpha > 0.01) {
          ctx.save();
          ctx.globalAlpha = alpha;
          const pulse = 1 + Math.sin(now / 200) * 0.05;
          const flash = rw.hitFlash > 0 ? 0.4 : 0;
          const bodyR = baseR * pulse;
          // Outer halo
          const bg = ctx.createRadialGradient(cx, cy, 2, cx, cy, bodyR * 1.6);
          bg.addColorStop(0, "rgba(255,0,255,1)");
          bg.addColorStop(0.4, "rgba(58,10,102,0.95)");
          bg.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = bg;
          ctx.beginPath();
          ctx.arc(cx, cy, bodyR * 1.6, 0, Math.PI * 2);
          ctx.fill();
          // Core
          ctx.fillStyle = flash > 0 ? "#ffffff" : "#0a0014";
          ctx.beginPath();
          ctx.arc(cx, cy, bodyR * 0.7, 0, Math.PI * 2);
          ctx.fill();
          // Inner magenta highlight
          ctx.fillStyle = `rgba(255,0,255,${0.85 - flash})`;
          ctx.beginPath();
          ctx.arc(cx - bodyR * 0.18, cy - bodyR * 0.18, bodyR * 0.35, 0, Math.PI * 2);
          ctx.fill();
          // HP pips — small ringed dots above the body.
          for (let i = 0; i < rw.maxHp; i++) {
            const pipX = cx + (i - (rw.maxHp - 1) / 2) * 10;
            const pipY = cy - bodyR - 10;
            ctx.fillStyle = i < rw.hp ? "#ff00ff" : "#3a0a66";
            ctx.beginPath();
            ctx.arc(pipX, pipY, 3, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = "rgba(255,255,255,0.6)";
            ctx.lineWidth = 1;
            ctx.stroke();
          }
          ctx.restore();
        }
      }
      // Worm plasma bolts — slow magenta projectiles falling toward
      // the paddle. Drawn after the rare worm so they appear to come
      // OUT of it.
      if (s.wormBolts && s.wormBolts.length) {
        ctx.save();
        for (const bolt of s.wormBolts) {
          ctx.fillStyle = "rgba(255,0,255,0.9)";
          ctx.shadowBlur = 16;
          ctx.shadowColor = "#ff00ff";
          ctx.beginPath();
          ctx.arc(bolt.x, bolt.y, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
          ctx.fillStyle = "rgba(255,255,255,0.85)";
          ctx.beginPath();
          ctx.arc(bolt.x, bolt.y, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      // Worm Swarm — small green segmented worms hunting bricks. Each
      // entry has a `segments` trail of recent head positions so they
      // render with the same wormy aesthetic as the player ball.
      // After unlocking the Worm Tamer achievement, swarms render in
      // the gold-green "Mass Extinction" palette — pure cosmetic flex.
      if (s.wormSwarms && s.wormSwarms.length) {
        const isMassExtinction = !!walletCfgRef.current.wormTamerUnlocked;
        const tailColor = isMassExtinction ? "#ffea3a" : "#00ff66";
        const headColor = isMassExtinction ? "#fffadc" : "#bbffd3";
        const haloColor = isMassExtinction ? "#ffd700" : "#00ff66";
        const haloBlur = isMassExtinction ? 14 : 8;
        ctx.save();
        for (const w of s.wormSwarms) {
          // Tail: progressively dimmer, smaller dots from oldest to newest.
          for (let si = w.segments.length - 1; si >= 0; si--) {
            const seg = w.segments[si];
            const t = 1 - si / Math.max(1, w.segments.length);
            const r = 1.5 + 2.2 * t;
            ctx.globalAlpha = 0.25 + 0.65 * t;
            ctx.fillStyle = tailColor;
            ctx.beginPath();
            ctx.arc(seg.x, seg.y, r, 0, Math.PI * 2);
            ctx.fill();
          }
          // Head: bright gold/green dot with shadow halo so it pops on dark bg.
          ctx.globalAlpha = 1;
          ctx.shadowBlur = haloBlur;
          ctx.shadowColor = haloColor;
          ctx.fillStyle = headColor;
          ctx.beginPath();
          ctx.arc(w.x, w.y, isMassExtinction ? 4 : 3.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
        ctx.restore();
      }
      drawPaddle(ctx, s.paddle, activeSkin, paddleAccent);
      s.balls.forEach((b) => drawWorm(ctx, b, ballSkinRef.current));
      // Halloween Pumpkin Bombs — only active when the pilot owns the
      // Halloween 2026 bundle AND has equipped the halloween-comet
      // skin. Spawns a small kawaii pumpkin at each in-flight ball
      // position on a fixed cadence; bombs fall under gravity and
      // explode with a spooky boom when they hit the floor.
      if (halloweenOwnedRef.current && ballSkinRef.current?.id === "halloween-comet") {
        const nowMs = gameNow();
        const dtMs = pumpkinBombsRef.lastDrawMs
          ? Math.min(64, nowMs - pumpkinBombsRef.lastDrawMs)
          : 16;
        pumpkinBombsRef.lastDrawMs = nowMs;
        s.balls.forEach((b) => maybeSpawnPumpkinBomb(pumpkinBombsRef, b, dtMs, nowMs));
        tickAndDrawPumpkinBombs(ctx, pumpkinBombsRef, dtMs, PADDLE_Y - 4, nowMs);
      } else if (pumpkinBombsRef.current.length) {
        // Skin was un-equipped mid-run → clear any inflight bombs.
        clearPumpkinBombs(pumpkinBombsRef);
      }
      // Trick-Or-Treat candy drops — decorative sprite rain that runs
      // ONLY during daily play in October. No pumpkin-bundle gate —
      // the event is a universal Halloween treat.
      if (isDaily && trickOrTreatActiveRef.current) {
        const nowMs = gameNow();
        const dtMs = candyDropsRef.lastDrawMs
          ? Math.min(64, nowMs - candyDropsRef.lastDrawMs)
          : 16;
        candyDropsRef.lastDrawMs = nowMs;
        maybeSpawnCandy(candyDropsRef, W, nowMs);
        tickAndDrawCandy(ctx, candyDropsRef, dtMs, PADDLE_Y - 4, nowMs);
      } else if (candyDropsRef.current.length) {
        clearCandy(candyDropsRef);
      }
      drawParticles(ctx, s);
      // Floating "+1 ☾" ticks — spawned on stone catch, drift up + fade.
      if (endless && s.stoneTicks && s.stoneTicks.length) {
        drawStoneTicks(ctx, s.stoneTicks, gameNow(), getRenderPalette());
      }
      // Comet Storm streaks — orange diagonal tracers painted just before
      // the 30% brick destruction fires. Drawn above the bricks so they
      // visually "land on" the field.
      if (endless && cometStormRef.current.streaks.length) {
        drawCometStreaks(ctx, cometStormRef.current.streaks, gameNow());
      }
      // Black hole VFX draws on top of everything so it reads even during a
      // multiball burst. Expired holes are filtered out.
      s.blackHoles = s.blackHoles.filter((h) => drawBlackHole(ctx, h));
      drawSlowOverlay(ctx, s);
      // Moonstone power-up screen tints — violet vignette (Phase), cyan
      // pulsing border (Dilation), pink scanlines (Plasma). Painted last
      // so they overlay every gameplay element. Sourced from the gameNow
      // clock so they freeze during pause alongside the timer logic.
      if (endless) {
        const fx = moonstoneFxRef.current;
        const now = gameNow();
        drawFxOverlays(ctx, W, H, {
          phaseActive: fx.phaseUntil > now,
          dilationActive: fx.dilationUntil > now,
          plasmaActive: fx.plasmaUntil > now,
          t: now / 1000,
        });
        // Activation flash — short, intense colour pulse painted on top
        // of the ambient tint. Cleared once expired so it doesn't keep
        // re-evaluating in the loop.
        if (fxFlashRef.current) {
          drawFxFlash(ctx, W, H, fxFlashRef.current, now);
          if (fxFlashRef.current.until <= now) fxFlashRef.current = null;
        }
      }

      // BLOOD SPLATTER overlay — drawn LAST so it sits on top of every
      // gameplay layer (bricks, paddle, ball, HUD-in-canvas overlays).
      // Each splat decays from full alpha → 0 over its `life` ms and is
      // pruned in-place once expired.
      if (s.bloodSplats && s.bloodSplats.length) {
        const now = performance.now();
        ctx.save();
        for (let i = s.bloodSplats.length - 1; i >= 0; i--) {
          const sp = s.bloodSplats[i];
          const age = now - sp.bornAt;
          if (age >= sp.life) { s.bloodSplats.splice(i, 1); continue; }
          // Quick fade-in (first 60ms feels like an impact), then slow
          // linear decay over the rest of the lifetime.
          const fadeIn = Math.min(1, age / 60);
          const decay = 1 - Math.max(0, (age - 60) / Math.max(1, sp.life - 60));
          const alpha = Math.max(0, Math.min(1, fadeIn * decay)) * 0.85;
          ctx.globalAlpha = alpha;
          ctx.translate(sp.x, sp.y);
          ctx.rotate(sp.rot);
          // Outer dark-blood glow
          ctx.shadowBlur = 8;
          ctx.shadowColor = "#5b0010";
          // Main blob — multi-layer ellipse for irregular shape.
          ctx.fillStyle = "#8a0014";
          ctx.beginPath();
          ctx.ellipse(0, 0, sp.r, sp.r * 0.82, 0, 0, Math.PI * 2);
          ctx.fill();
          // Slightly brighter core.
          ctx.shadowBlur = 0;
          ctx.fillStyle = "#b3001f";
          ctx.beginPath();
          ctx.ellipse(0, 0, sp.r * 0.72, sp.r * 0.58, 0, 0, Math.PI * 2);
          ctx.fill();
          // Lighter inner highlight.
          ctx.fillStyle = "#ff1f3a";
          ctx.beginPath();
          ctx.ellipse(-sp.r * 0.18, -sp.r * 0.12, sp.r * 0.32, sp.r * 0.24, 0, 0, Math.PI * 2);
          ctx.fill();
          // Drip tails — short tear-drops dangling from the splat.
          if (sp.drips > 0) {
            ctx.fillStyle = "#8a0014";
            for (let d = 0; d < sp.drips; d++) {
              const dripAng = (d / sp.drips) * Math.PI * 2 + sp.rot * 0.7;
              const dripLen = sp.r * (0.7 + Math.random() * 0.8);
              const dripX = Math.cos(dripAng) * sp.r * 0.7;
              const dripY = Math.sin(dripAng) * sp.r * 0.7;
              ctx.beginPath();
              ctx.ellipse(
                dripX + Math.cos(dripAng) * dripLen * 0.5,
                dripY + Math.sin(dripAng) * dripLen * 0.5,
                Math.max(1.5, sp.r * 0.08),
                dripLen * 0.5,
                dripAng,
                0,
                Math.PI * 2,
              );
              ctx.fill();
            }
          }
          // Tiny micro-droplets around the main blob.
          ctx.fillStyle = "#b3001f";
          for (let m = 0; m < 4; m++) {
            const mAng = Math.random() * Math.PI * 2;
            const mDist = sp.r * (0.9 + Math.random() * 0.6);
            ctx.beginPath();
            ctx.arc(
              Math.cos(mAng) * mDist,
              Math.sin(mAng) * mDist,
              Math.max(0.7, Math.random() * sp.r * 0.08),
              0,
              Math.PI * 2,
            );
            ctx.fill();
          }
          // Reset transform for next splat.
          ctx.rotate(-sp.rot);
          ctx.translate(-sp.x, -sp.y);
        }
        ctx.restore();
      }
    };

    let last = performance.now();

    // Once-per-mount latch — ensures we don't spam the telemetry endpoint
    // when the rAF loop throws every frame. Backend also rate-limits per
    // user/surface (30s window), but client-side suppression keeps us
    // from generating useless network traffic in a flapping crash.
    let errorReported = false;

    // Boss-zoom helper — returns the current zoom factor (1.0 when no
    // boss level is active). Held at 1.06× during the entrance
    // cinematic, then linearly eases back to 1.0× over ~1.4s of
    // gameplay so the pilot has the predator framed up close before
    // returning to standard play scale.
    const computeBossZoom = () => {
      const start = bossZoomStartRef.current;
      if (start <= 0) return 1;
      const elapsed = (performance.now() - start) / 1000;
      if (elapsed < 2.4) return 1.06;          // hold during cinematic
      if (elapsed < 3.8) {                      // linear pull-out
        return 1.06 - ((elapsed - 2.4) / 1.4) * 0.06;
      }
      return 1;                                 // settled
    };
    const renderWorldWithZoom = (s) => {
      const zoom = computeBossZoom();
      if (zoom !== 1) {
        ctx.save();
        ctx.translate(W / 2, H / 2);
        ctx.scale(zoom, zoom);
        ctx.translate(-W / 2, -H / 2);
        drawBackground(ctx, s);
        drawFrame(s);
        ctx.restore();
      } else {
        drawBackground(ctx, s);
        drawFrame(s);
      }
    };

    const loop = (t) => {
      try {
        const dt = Math.min(32, t - last);
        last = t;
        const s = stateRef.current;
        if (!s) return;

        if (paused || gameOver || won) {
          renderWorldWithZoom(s);
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
        // Original loop body continues at the closing `} catch ...` below.
        return loopBody(t, dt, s);
      } catch (err) {
        // The rAF loop has no surface error reporting — without this
        // wrapper, a ReferenceError silently breaks the game until
        // someone screenshots it. Fire-and-forget POST so dev/ops can
        // see exactly what crashed in the /api/_client-error feed.
        if (!errorReported) {
          errorReported = true;
          try {
            apiClient.post("/_client-error", {
              message: String(err?.message || err).slice(0, 500),
              stack: String(err?.stack || "").slice(0, 4000),
              surface: "game-loop",
              url: window.location.pathname,
              user_agent: (navigator.userAgent || "").slice(0, 200),
            }).catch(() => {});
          } catch (_inner) { /* never throw out of catch */ }
        }
        // Re-throw so devs see the same red overlay in dev mode and
        // the rAF loop terminates cleanly instead of partial-rendering.
        throw err;
      }
    };

    const loopBody = (t, dt, s) => {
      void t;
      // Brick rotations were retired in May 2026 — see `buildBlocks`.
      // Bricks now stay static so each level reads as a coherent space
      // shape instead of a spinning chaos field. `dt` is still used by
      // every other tick below.
      void dt;

      // Comet Storm — wave-10 trigger spawns N animated streaks + a
      // `destroyAt` timestamp. The loop draws the streaks every frame
      // (via drawFrame) and triggers the destruction the moment
      // `destroyAt` is reached, reusing spawnPowerup + spawnMoonstone.
      // Boosted milestone fires destroy a higher % and shake harder.
      const cstorm = cometStormRef.current;
      if (cstorm.destroyAt > 0 && gameNow() >= cstorm.destroyAt) {
        cstorm.destroyAt = 0;
        const ratio = Number.isFinite(cstorm.destroyRatio) ? cstorm.destroyRatio : 0.30;
        const isBoosted = !!cstorm.boosted;
        // 360ms @ magnitude 10 for boosted, 200ms @ magnitude 6 default.
        cameraShakeRef.current = {
          until: gameNow() + (isBoosted ? 360 : 200),
          magnitude: isBoosted ? 10 : 6,
        };
        if (s.blocks.length) {
          const target = Math.max(1, Math.floor(s.blocks.length * ratio));
          const idxs = s.blocks.map((_, i) => i);
          for (let i = idxs.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [idxs[i], idxs[j]] = [idxs[j], idxs[i]];
          }
          const toDestroy = idxs.slice(0, target).sort((a, b) => b - a);
          let dropScore = 0;
          for (const idx of toDestroy) {
            const b = s.blocks[idx];
            if (!b) continue;
            const color = TIER_COLORS[b.tier];
            spawnParticles(b.x + b.w / 2, b.y + b.h / 2, color);
            spawnPowerup(b.x + b.w / 2, b.y + b.h / 2);
            spawnMoonstone(b.x + b.w / 2, b.y + b.h / 2);
            // Boosted fires award 1.5x the per-brick points for the
            // bigger swing — encourages timing the milestone for a
            // dense board.
            dropScore += Math.round(50 * b.tier * (isBoosted ? 1.5 : 1));
            if (metricsRef.current) metricsRef.current.blocksDestroyed += 1;
            s.blocks.splice(idx, 1);
          }
          if (dropScore) setScore((sc) => sc + dropScore);
          Sounds.destroyBlock();
        }
      }

      // Boss cluster oscillation — sin-driven horizontal sway so the boss
      // feels alive. Updates `b.x` (the same property collisions read), so
      // the AABB stays accurate without a separate "render offset". Only
      // the cluster bricks (`bossPart=true`) move; flank bricks stay put.
      if (levelCfg.boss) {
        const bossPhase = (t || 0) * 0.0008;            // ~3.5s per cycle
        const bossOffset = Math.sin(bossPhase) * 26;     // ±26px sway
        for (let i = 0; i < s.blocks.length; i++) {
          const blk = s.blocks[i];
          if (blk.bossPart) blk.x = blk.baseX + bossOffset;
        }
      }

      // Paddle movement
      const paddleSpeed = 8 * (dt / 16);
      let moved = false;
      if (mouseXRef.current != null) {
        const target = mouseXRef.current - s.paddle.w / 2;
        const clamped = Math.max(10, Math.min(W - 10 - s.paddle.w, target));
        if (Math.abs(clamped - s.paddle.x) > 0.1) {
          s.paddle.x = clamped;
          moved = true;
        }
      }
      if (keysRef.current["ArrowLeft"] || keysRef.current["a"] || keysRef.current["A"]) {
        s.paddle.x -= paddleSpeed;
        mouseXRef.current = null;
        moved = true;
      }
      if (keysRef.current["ArrowRight"] || keysRef.current["d"] || keysRef.current["D"]) {
        s.paddle.x += paddleSpeed;
        mouseXRef.current = null;
        moved = true;
      }
      s.paddle.x = Math.max(10, Math.min(W - 10 - s.paddle.w, s.paddle.x));
      if (moved && Math.random() < 0.06) Sounds.paddleMove();

      if (s.paddle.enlargedUntil && performance.now() > s.paddle.enlargedUntil) {
        s.paddle.w = BASE_PADDLE_W;
        s.paddle.enlargedUntil = 0;
      }

      const slowActive = performance.now() < s.slowUntil;
      // Time Dilation (moonstone): extreme slow at 10% speed for 4s,
      // overrides the normal 50% slow-mo power-up.
      const dilationActive = endless && moonstoneFxRef.current.dilationUntil > gameNow();
      const speedMul = dilationActive ? 0.1 : (slowActive ? 0.5 : 1);

      // Balls
      for (const ball of s.balls) {
        if (ball.stuck) {
          ball.x = s.paddle.x + s.paddle.w / 2;
          ball.y = PADDLE_Y - BALL_RADIUS - 2;
          ball.trail = [];
          continue;
        }
        ball.trail.push({ x: ball.x, y: ball.y });
        if (ball.trail.length > 12) ball.trail.shift();
        ball.x += ball.vx * speedMul * (dt / 16);
        ball.y += ball.vy * speedMul * (dt / 16);

        // Walls
        if (ball.x < 10 + BALL_RADIUS) {
          ball.x = 10 + BALL_RADIUS;
          ball.vx = Math.abs(ball.vx);
          Sounds.bounceWall();
        } else if (ball.x > W - 10 - BALL_RADIUS) {
          ball.x = W - 10 - BALL_RADIUS;
          ball.vx = -Math.abs(ball.vx);
          Sounds.bounceWall();
        }
        if (ball.y < 10 + BALL_RADIUS) {
          ball.y = 10 + BALL_RADIUS;
          ball.vy = Math.abs(ball.vy);
          Sounds.bounceWall();
        }

        // Paddle
        if (
          ball.vy > 0 &&
          ball.y + BALL_RADIUS >= PADDLE_Y &&
          ball.y - BALL_RADIUS <= PADDLE_Y + PADDLE_H &&
          ball.x >= s.paddle.x &&
          ball.x <= s.paddle.x + s.paddle.w
        ) {
          const relative = (ball.x - s.paddle.x) / s.paddle.w - 0.5;
          const speed = Math.hypot(ball.vx, ball.vy);
          const angle = -Math.PI / 2 + relative * 1.1;
          ball.vx = Math.cos(angle) * speed;
          ball.vy = Math.sin(angle) * speed;
          ball.y = PADDLE_Y - BALL_RADIUS - 1;
          Sounds.bouncePaddle();
        }

        // Blocks (AABB vs circle)
        for (let i = s.blocks.length - 1; i >= 0; i--) {
          const b = s.blocks[i];
          if (
            ball.x + BALL_RADIUS > b.x &&
            ball.x - BALL_RADIUS < b.x + b.w &&
            ball.y + BALL_RADIUS > b.y &&
            ball.y - BALL_RADIUS < b.y + b.h
          ) {
            // Phase Ball (moonstone power-up OR Phase Comet artifact):
            // the ball passes through bricks instead of bouncing. Each
            // overlap instantly destroys the block, awards the kill
            // score, and continues — supports ripping through clusters
            // in one pass. The artifact charge is consumed once per
            // pierce-pass; the latch survives until the ball clears the
            // row (vy flips upward → ball travelled all the way through).
            let phaseActive = endless && moonstoneFxRef.current.phaseUntil > gameNow();
            if (!phaseActive && ball._phaseLatched) {
              phaseActive = true;
            } else if (!phaseActive) {
              if (artifactPowers.tryPhaseSkip(artifactsRef.current)) {
                phaseActive = true;
                ball._phaseLatched = true;
                ball._phaseLatchedAt = gameNow();
                spawnParticles(b.x + b.w / 2, b.y + b.h / 2, "#7df9ff");
              }
            }
            if (!phaseActive) {
              const overlapX = Math.min(ball.x + BALL_RADIUS - b.x, b.x + b.w - (ball.x - BALL_RADIUS));
              const overlapY = Math.min(ball.y + BALL_RADIUS - b.y, b.y + b.h - (ball.y - BALL_RADIUS));
              if (overlapX < overlapY) ball.vx = -ball.vx;
              else ball.vy = -ball.vy;
            }

            b.hp = phaseActive ? 0 : b.hp - 1;
            if (b.hp <= 0) {
              const color = TIER_COLORS[b.tier];
              spawnParticles(b.x + b.w / 2, b.y + b.h / 2, color);
              spawnPowerup(b.x + b.w / 2, b.y + b.h / 2);
              spawnMoonstone(b.x + b.w / 2, b.y + b.h / 2);
              const srcX = b.x + b.w / 2;
              const srcY = b.y + b.h / 2;
              s.blocks.splice(i, 1);
              bumpCombo(1);
              setScore((sc) => sc + Math.round(50 * b.tier * comboMultiplier(comboRef.current.count)));
              if (metricsRef.current) {
                metricsRef.current.blocksDestroyed += 1;
                if (b.tier === 4) metricsRef.current.bossKilled = true;
              }
              Sounds.destroyBlock();
              // --- Artifact powers: Bomb + Chain (ball) ---
              // Bomb Comet: every Nth brick destroy triggers a small
              // AoE explosion that wipes nearby bricks.
              const bomb = artifactPowers.maybeTriggerBomb(
                artifactsRef.current, s.blocks, srcX, srcY, -1
              );
              if (bomb && bomb.indices.length) {
                // Splice in descending order so subsequent indices stay valid.
                bomb.indices.sort((a, c) => c - a).forEach((idx) => {
                  const bb = s.blocks[idx];
                  if (!bb) return;
                  spawnParticles(bb.x + bb.w / 2, bb.y + bb.h / 2, "#ff8c00");
                  spawnMoonstone(bb.x + bb.w / 2, bb.y + bb.h / 2);
                  s.blocks.splice(idx, 1);
                  setScore((sc) => sc + Math.round(50 * bb.tier));
                  if (metricsRef.current) metricsRef.current.blocksDestroyed += 1;
                });
                // Visual ring burst at the explosion center.
                for (let k = 0; k < 12; k++) {
                  spawnParticles(
                    srcX + Math.cos((k / 12) * Math.PI * 2) * 18,
                    srcY + Math.sin((k / 12) * Math.PI * 2) * 18,
                    "#ffb347"
                  );
                }
                Sounds.destroyBlock();
                // ----- Bomb + Chain Lightning SYNERGY -----
                // After the primary blast, the bomb's combo aftermath
                // arcs lightning to a handful of nearby neighbors
                // OUTSIDE the bomb radius. `chainIndices` is in
                // descending order is not guaranteed, so we sort
                // before splicing. Per-arc visual streaks are pushed
                // to a transient `bombArcsRef` ring buffer that the
                // render loop draws for ~280ms.
                if (bomb.chainIndices && bomb.chainIndices.length) {
                  const targets = bomb.chainIndices
                    .slice()
                    .sort((a, c) => c - a);
                  targets.forEach((idx) => {
                    const cb = s.blocks[idx];
                    if (!cb) return;
                    spawnParticles(cb.x + cb.w / 2, cb.y + cb.h / 2, "#67e8f9");
                    spawnParticles(cb.x + cb.w / 2, cb.y + cb.h / 2, "#ffffff");
                    spawnMoonstone(cb.x + cb.w / 2, cb.y + cb.h / 2);
                    s.blocks.splice(idx, 1);
                    setScore((sc) => sc + Math.round(50 * cb.tier));
                    if (metricsRef.current) metricsRef.current.blocksDestroyed += 1;
                  });
                  // Push the arcs onto the visual buffer with an
                  // expiry timestamp (~280ms). The renderer is wired
                  // up below in the draw pass.
                  const now = performance.now();
                  (bomb.arcs || []).forEach((arc) => {
                    bombArcsRef.current.push({
                      ...arc,
                      bornAt: now,
                      ttlMs: 280,
                    });
                  });
                  Sounds.destroyBlock();
                }
              }
              // Chain Comet: chain to the nearest brick if within window.
              const chain = artifactPowers.maybeTriggerChain(
                artifactsRef.current, s.blocks, srcX, srcY, -1, performance.now()
              );
              if (chain && chain.index >= 0) {
                const cb = s.blocks[chain.index];
                if (cb) {
                  spawnParticles(cb.x + cb.w / 2, cb.y + cb.h / 2, "#ff007f");
                  spawnMoonstone(cb.x + cb.w / 2, cb.y + cb.h / 2);
                  s.blocks.splice(chain.index, 1);
                  setScore((sc) => sc + Math.round(50 * cb.tier));
                  if (metricsRef.current) metricsRef.current.blocksDestroyed += 1;
                }
              }
            } else {
              setScore((sc) => sc + 10);
              Sounds.hitBlock();
            }
            // Phase Ball keeps scanning subsequent overlaps so a single
            // frame can chain-destroy clustered bricks; non-phase break.
            if (!phaseActive) break;
          }
        }
        // Once the ball clears all brick collisions for this frame,
        // unlatch the Phase Comet hold if 250ms have passed since the
        // pierce started (≈ time for the ball to fully exit a brick
        // row). One charge therefore = one full pass through a row.
        if (ball._phaseLatched && gameNow() - (ball._phaseLatchedAt || 0) > 250) {
          ball._phaseLatched = false;
        }

        // Space Worm collision — ball vs free-roaming worm. Use a circle-
        // vs-circle test against the worm's body radius so the silhouette
        // Rare Wormhole Worm collision — Endless-only. Only damages
        // the worm during `idle` / `fade-in` phases (when solid). The
        // ball deflects on hit. On HP=0 BEFORE bricks are cleared, set
        // the slain-before-clear flag so the level-clear handler can
        // claim the ultra-rare Singularity ball skin trophy.
        if (s.rareWorm && (s.rareWorm.phase === "idle" || s.rareWorm.phase === "fade-in")) {
          const rw = s.rareWorm;
          const bodyR = 22;
          const ddx = ball.x - rw.x;
          const ddy = ball.y - rw.y;
          const ddistSq = ddx * ddx + ddy * ddy;
          const dcollideR = bodyR + BALL_RADIUS;
          if (ddistSq < dcollideR * dcollideR) {
            rw.hp -= 1;
            rw.hitFlash = 14;
            spawnParticles(rw.x, rw.y, "#ff00ff");
            spawnParticles(rw.x, rw.y, "#3a0a66");
            Sounds.brick?.();
            // Deflect the ball.
            const dist = Math.sqrt(ddistSq) || 1;
            const nx = ddx / dist;
            const ny = ddy / dist;
            const dot = ball.vx * nx + ball.vy * ny;
            ball.vx -= 2 * dot * nx;
            ball.vy -= 2 * dot * ny;
            // Speed nudge to keep the encounter punchy.
            ball.vx *= 1.02;
            ball.vy *= 1.02;
            if (rw.hp <= 0) {
              // Check: are there still bricks left to clear? If yes,
              // the pilot earned the Wormhole Slayer trophy.
              const bricksRemaining = s.blocks.some(
                (bl) => !bl.dead && !bl.unbreakable,
              );
              if (bricksRemaining) {
                s.rareWormSlainBeforeClear = true;
              }
              // Big celebratory burst — magenta + cyan + gold.
              spawnParticles(rw.x, rw.y, "#ff00ff");
              spawnParticles(rw.x, rw.y, "#00ffff");
              spawnParticles(rw.x, rw.y, "#ffd700");
              Sounds.bossDefeat?.();
              s.rareWorm = null;
              // Score bonus — flat 2500 pts for the rare kill.
              setScore((sc) => sc + 2500);
              // Trigger an async trophy claim — only the FIRST kill in
              // a level needs it; the backend dedupes by UTC day.
              if (bricksRemaining) {
                // Fire-and-forget — don't block the rAF loop.
                const wave = endless ? endlessWave : 1;
                apiClient.post("/endless/wormhole-kill", { wave }).then((r) => {
                  if (r?.data?.awarded) {
                    toast.success("ULTRA-RARE · Wormhole Slayer skin unlocked", {
                      description: "Equip the Singularity skin from the Skins menu.",
                      icon: <Sparkles className="w-4 h-4 text-[#ff00ff]" />,
                      duration: 8000,
                    });
                  }
                }).catch(() => {
                  // Already-claimed-today / network blip — silent.
                });
              }
            }
          }
        }


        // Space Worm collision — ball vs free-roaming worm. Use a circle-
        // vs-circle test against the worm's body radius so the silhouette
        // matches the visible chrome-blue arc. On hit: -1 hp, deflect ball
        // (only when NOT phase-active), spawn a chrome-blue particle
        // burst, and flash the worm. On kill: bigger burst, score, sound,
        // bonus moonstones (small=3, giant=8), and remove the worm.
        if (s.spaceWorms && s.spaceWorms.length) {
          const phaseActive = endless && moonstoneFxRef.current.phaseUntil > gameNow();
          for (let wi = s.spaceWorms.length - 1; wi >= 0; wi--) {
            const sw = s.spaceWorms[wi];
            const bodyR = sw.kind === "giant" ? 56 : sw.kind === "skeleton" ? 22 : 32;
            const dx = ball.x - sw.x;
            const dy = ball.y - sw.y;
            const distSq = dx * dx + dy * dy;
            const collideR = bodyR + BALL_RADIUS;
            if (distSq < collideR * collideR) {
              // Damage applies once per frame per worm even if phase-active.
              sw.hp -= 1;
              sw.hitFlash = 12; // frames of magenta/cyan flash
              // Skeleton worms scatter bone-cyan particles; Leviathans
              // keep the violet/magenta scheme.
              if (sw.kind === "skeleton") {
                spawnParticles(sw.x, sw.y, "#aef0ff");
                spawnParticles(sw.x, sw.y, "#7c8a9c");
              } else {
                spawnParticles(sw.x, sw.y, "#c95fff");
                spawnParticles(sw.x, sw.y, "#3d1c5a");
              }
              // Skeleton worms get their own bone-clatter hit SFX.
              // Leviathans keep the generic block-hit thunk.
              if (sw.kind === "skeleton") {
                Sounds.skeletonHit();
              } else {
                Sounds.hitBlock();
              }
              if (sw.hp <= 0) {
                // Big death burst — colour matches the kind.
                for (let bi = 0; bi < 6; bi++) {
                  const c1 = sw.kind === "skeleton" ? "#aef0ff" : "#c95fff";
                  const c2 = sw.kind === "skeleton" ? "#ffffff" : "#ff8eff";
                  spawnParticles(
                    sw.x + (Math.random() - 0.5) * bodyR,
                    sw.y + (Math.random() - 0.5) * bodyR,
                    bi % 2 === 0 ? c1 : c2,
                  );
                }
                const bonus = sw.kind === "giant"
                  ? 2500
                  : sw.kind === "skeleton"
                    ? 250
                    : 750;
                setScore((sc) => sc + bonus);
                if (metricsRef.current) {
                  metricsRef.current.blocksDestroyed += sw.kind === "giant" ? 8 : sw.kind === "skeleton" ? 1 : 3;
                }
                // Drop a few moonstones in Endless so the boss is also
                // economically rewarding. In regular play, drop one
                // power-up to the same effect.
                if (endless) {
                  const stoneCount = sw.kind === "giant" ? 8 : sw.kind === "skeleton" ? 1 : 3;
                  for (let mi = 0; mi < stoneCount; mi++) {
                    spawnMoonstone(sw.x + (Math.random() - 0.5) * 40, sw.y + (Math.random() - 0.5) * 20);
                  }
                } else {
                  spawnPowerup(sw.x, sw.y);
                  if (sw.kind === "giant") spawnPowerup(sw.x + 20, sw.y);
                }
                // Kill SFX — skeletons get a cracked-skull thunk;
                // Leviathans keep the generic destroy chime (their bigger
                // deaths come with their own bossDefeat cinematic).
                if (sw.kind === "skeleton") {
                  Sounds.skeletonKill();
                } else {
                  Sounds.destroyBlock();
                }
                // Worm Hunter — flush one kill credit. Fire-and-forget;
                // the server returns newly-unlocked achievements which
                // we surface as toasts. Cap count at 1 since a worm dies
                // exactly once per call. Skeleton kills route to the
                // Bone Reaper counter on the new endpoint instead so the
                // two species' lifetime stats stay separate.
                const killEndpoint = sw.kind === "skeleton"
                  ? "/achievements/skeleton-worm-kills"
                  : "/achievements/space-worm-kills";
                apiClient
                  .post(killEndpoint, { count: 1 })
                  .then((r) => {
                    const newly = r.data?.newly_unlocked_achievements || [];
                    for (const a of newly) {
                      toast.success(`${a.name}`, {
                        description: `${a.desc} · +${(a.reward_score || 0).toLocaleString()} pts`,
                        duration: 5000,
                      });
                    }
                  })
                  .catch(() => {});
                // Bone Coin — perfect-clear bonus. When the pilot kills
                // EVERY skeleton in the swarm on the same life, drop a
                // single rare reward: 5 moonstones in Endless or a
                // guaranteed Multi-ball in regular play. Bypasses the
                // normal POWERUP_CHANCE gate so the reward feels earned.
                if (sw.kind === "skeleton") {
                  s.skelKilledThisLife = (s.skelKilledThisLife || 0) + 1;
                  // First-kill welcome toast — surfaces the hidden Bone
                  // Coin mechanic on a pilot's very first skeleton down.
                  // localStorage-gated so repeat pilots see their usual
                  // toast stream without noise. Intentionally one-shot;
                  // no dismiss button since toasts auto-expire.
                  try {
                    if (localStorage.getItem("cosmic_first_skeleton_seen") !== "1") {
                      localStorage.setItem("cosmic_first_skeleton_seen", "1");
                      toast.success("+250 · Your first Skeleton!", {
                        description: "Kill all 3 on one life for a Bone Coin bonus.",
                        duration: 5500,
                      });
                    }
                  } catch {
                    // privacy mode / quota — no harm, the Bone Coin hint
                    // in the Legend already covers this for players who
                    // open the overlay.
                  }
                  if (
                    !s.skelBonusGranted
                    && s.skelInitialCount > 0
                    && s.skelKilledThisLife >= s.skelInitialCount
                  ) {
                    s.skelBonusGranted = true;
                    if (endless) {
                      // 5 moonstones — drop them in a small fan from the
                      // last skeleton's position so the catch animation
                      // reads as one big payout, not five trickle drops.
                      for (let mi = 0; mi < 5; mi++) {
                        s.moonstones.push({
                          x: sw.x + (Math.random() - 0.5) * 60,
                          y: sw.y + (Math.random() - 0.5) * 20,
                          vy: MOONSTONE_FALL_SPEED,
                          r: MOONSTONE_RADIUS,
                          seed: Math.random() * Math.PI * 2,
                        });
                      }
                    } else {
                      // Regular play — drop a guaranteed Multi-ball
                      // power-up directly (skipping the normal chance
                      // roll). Falls toward the paddle like any other.
                      s.powerups.push({
                        x: sw.x,
                        y: sw.y,
                        kind: "multi",
                        vy: 1.8,
                      });
                    }
                    // Cyan/white celebratory burst around the kill site.
                    for (let bi = 0; bi < 14; bi++) {
                      spawnParticles(
                        sw.x + (Math.random() - 0.5) * 80,
                        sw.y + (Math.random() - 0.5) * 40,
                        bi % 2 === 0 ? "#aef0ff" : "#ffffff",
                      );
                    }
                    Sounds.win();
                    toast.success("BONE COIN!", {
                      description: endless
                        ? "Perfect skeleton clear · +5 ☾"
                        : "Perfect skeleton clear · Multi-Ball incoming",
                      duration: 4500,
                    });
                  }
                }
                s.spaceWorms.splice(wi, 1);
                // Post-mortem cinematic — fires when a regular-play
                // Leviathan dies (small or giant). Mirror of the entrance
                // cinematic but in gold/green with a "DEFEATED" tone.
                // Skipped in Endless (its `bossDefeat` SFX + wave banner
                // already cover the kill beat) and for skeletons (mini
                // bosses don't warrant a full-screen card).
                if (
                  !endless
                  && (sw.kind === "small" || sw.kind === "giant")
                  && !bossDefeatedCinematicRef.current
                ) {
                  // Skill rating S/A/B/C — based on lives remaining and
                  // time taken. Read directly from refs so the rAF closure
                  // sees the latest values without waiting on a re-render.
                  const startedAt = metricsRef.current?.startedAt || performance.now();
                  const elapsedS = Math.max(0, (performance.now() - startedAt) / 1000);
                  // Time targets are tier-aware: giants are tankier so they
                  // get more generous windows.
                  const target = sw.kind === "giant"
                    ? { s: 35, a: 50, b: 75 }
                    : { s: 22, a: 35, b: 55 };
                  const livesNow = livesRef.current ?? 3;
                  let rank = "C";
                  if (livesNow >= 3 && elapsedS < target.s) rank = "S";
                  else if (livesNow >= 2 && elapsedS < target.a) rank = "A";
                  else if (livesNow >= 1 && elapsedS < target.b) rank = "B";
                  const cfg = {
                    kind: sw.kind,
                    label: sw.kind === "giant" ? "APEX DEFEATED" : "LEVIATHAN SLAIN",
                    rank,
                    elapsedS: Math.round(elapsedS * 10) / 10,
                    durationMs: 1800,
                  };
                  bossDefeatedCinematicRef.current = cfg;
                  setBossDefeatedCinematic(cfg);
                  // Persist the achieved rank on the metrics record so
                  // the level-complete handler ships it to the server
                  // along with score + duration. Only kept if it beats
                  // any earlier rank from the same run (rare — bosses
                  // usually die once per level).
                  if (metricsRef.current) {
                    const priority = { S: 4, A: 3, B: 2, C: 1 };
                    const prev = metricsRef.current.bossRank;
                    if (!prev || (priority[rank] || 0) > (priority[prev] || 0)) {
                      metricsRef.current.bossRank = rank;
                    }
                  }
                  Sounds.bossDefeat();
                  setTimeout(() => {
                    bossDefeatedCinematicRef.current = null;
                    setBossDefeatedCinematic(null);
                  }, cfg.durationMs);
                }
                continue;
              }
              // Bounce — only deflect when NOT phasing (matches block contract)
              if (!phaseActive) {
                const len = Math.sqrt(distSq) || 1;
                const nx = dx / len, ny = dy / len;
                const dot = ball.vx * nx + ball.vy * ny;
                ball.vx = ball.vx - 2 * dot * nx;
                ball.vy = ball.vy - 2 * dot * ny;
                // Push the ball just outside the body so it doesn't re-hit
                // on the same frame.
                ball.x = sw.x + nx * (collideR + 1);
                ball.y = sw.y + ny * (collideR + 1);
              }
            }
          }
        }
      }

      // Lost balls — Star Shield (moonstone) intercepts the FIRST life-loss
      // event and respawns one fresh ball at the paddle, consuming the
      // shield charge. No life is deducted on a shield save.
      s.balls = s.balls.filter((b) => b.y < H + 20);
      setBallsLeft(s.balls.length);
      if (metricsRef.current && s.balls.length > metricsRef.current.maxBalls) {
        metricsRef.current.maxBalls = s.balls.length;
      }
      // Combo timeout — if no brick was destroyed within COMBO_WINDOW_MS,
      // reset the chain. Cheap per-frame check; setComboCount only fires
      // when the count was non-zero.
      if (comboRef.current.count > 0 &&
          performance.now() - comboRef.current.lastAt > COMBO_WINDOW_MS) {
        resetCombo();
      }
      if (s.balls.length === 0) {
        // Aegis Paddle artifact — non-Endless save attempt. Spawns a
        // fresh ball at the paddle, consuming one charge per save (per
        // life). Endless still uses Star Shield first; aegis fires next
        // when shield isn't armed.
        const aegisCanSave =
          !(endless && moonstoneFxRef.current.shieldArmed) &&
          artifactPowers.tryAegisSave(artifactsRef.current);
        if (aegisCanSave) {
          const speed = levelCfg.ballSpeed;
          const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.4;
          s.balls.push({
            x: s.paddle.x + s.paddle.w / 2,
            y: PADDLE_Y - BALL_RADIUS - 2,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            trail: [],
            stuck: false,
          });
          spawnParticles(s.paddle.x + s.paddle.w / 2, PADDLE_Y - 10, "#28e070");
          Sounds.extraLife();
          setBallsLeft(1);
        } else if (endless && moonstoneFxRef.current.shieldArmed) {
          // Save: pop a fresh ball back at the paddle, consume the shield.
          const speed = levelCfg.ballSpeed;
          const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.4;
          s.balls.push({
            x: s.paddle.x + s.paddle.w / 2,
            y: PADDLE_Y - BALL_RADIUS - 2,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            trail: [],
            stuck: false,
          });
          spawnParticles(s.paddle.x + s.paddle.w / 2, PADDLE_Y - 10, "#ffd700");
          setMoonstoneFx((fx) => ({ ...fx, shieldArmed: false }));
          Sounds.extraLife();
          setBallsLeft(1);
        } else {
          setLives((l) => {
            const nl = l - 1;
            if (metricsRef.current) metricsRef.current.lostLife = true;
            // Combo chain breaks on every life loss — pilots have to
            // earn the multiplier back from scratch.
            resetCombo();
            // BOOST STREAK also breaks on every life loss. Toast only
            // when the broken streak was non-trivial (≥2) so we don't
            // spam pilots who lose lives before getting a streak going.
            if (boostStreakRef.current.count >= 2) {
              toast.error(`STREAK BROKEN · was ×${boostStreakRef.current.count}`, {
                description: "Survive every milestone wave to chain the bonus.",
                duration: 2200,
              });
            }
            boostStreakRef.current.count = 0;
            // DAILY checkpoint — save lives + current score after every
            // life loss so a crash mid-attempt doesn't waste the
            // pilot's progress. Fire-and-forget; only attempts when
            // there's still life to resume INTO (nl >= 1). On total
            // game-over the natural retry flow already starts fresh.
            if (isDaily && nl >= 1) {
              try {
                setScore((curScore) => {
                  const snap = Math.round(curScore || 0);
                  apiClient.post("/daily/checkpoint", {
                    lives: Math.max(1, Math.min(9, nl)),
                    score: snap,
                  }).catch(() => {});
                  return curScore;
                });
              } catch {
                // Defensive — never let a checkpoint POST crash the canvas.
              }
            }
            // CAMPAIGN checkpoint — same softer-recovery pattern as Daily.
            // Saves lives + score per pilot, keyed by `level` server-side.
            // Only one campaign checkpoint exists at a time — switching
            // levels overwrites. Skipped for endless/daily/tournament
            // (mode === "tournament" sets endless=false but has its own
            // lifecycle, so gate on endless && isDaily NEGATIVE only).
            if (!endless && !isDaily && nl >= 1 && typeof level === "number") {
              try {
                setScore((curScore) => {
                  const snap = Math.round(curScore || 0);
                  apiClient.post("/campaign/checkpoint", {
                    level: Math.max(1, Math.min(3000, level)),
                    lives: Math.max(1, Math.min(9, nl)),
                    score: snap,
                  }).catch(() => {});
                  return curScore;
                });
              } catch {
                // Defensive — never let a checkpoint POST crash the canvas.
              }
            }
            // Bone Coin perfect-clear streak resets on life loss — the
            // pilot must kill all 3 skeletons cleanly on a single life.
            s.skelKilledThisLife = 0;
            // Reset per-life artifact state (aegis charges, phase
            // charges, bomb counter) so each life starts fresh.
            artifactPowers.resetPerLifeState(artifactsRef.current);
            if (nl <= 0) {
              setGameOver(true);
              Sounds.gameOver();
              stopMusic();
            } else {
              Sounds.loseLife();
              const speed = levelCfg.ballSpeed;
              const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.5;
              s.balls.push({
                x: W / 2,
                y: PADDLE_Y - BALL_RADIUS - 2,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                trail: [],
                stuck: true,
              });
            }
            return nl;
          });
        }
      }

      // Powerups
      for (let i = s.powerups.length - 1; i >= 0; i--) {
        const p = s.powerups[i];
        // Magnet Paddle artifact: pulls power-ups toward the paddle in
        // any non-competitive mode. No-op when artifact unequipped.
        artifactPowers.applyMagnetPull(
          artifactsRef.current, p, s.paddle.x + s.paddle.w / 2, dt
        );
        p.y += p.vy * (dt / 16);
        if (
          p.y + 14 >= PADDLE_Y &&
          p.y - 14 <= PADDLE_Y + PADDLE_H &&
          p.x >= s.paddle.x &&
          p.x <= s.paddle.x + s.paddle.w
        ) {
          applyPowerup(p.kind);
          if (metricsRef.current) metricsRef.current.powerupsCollected += 1;
          s.powerups.splice(i, 1);
        } else if (p.y > H + 30) {
          s.powerups.splice(i, 1);
        }
      }

      // Endless: Moonstones — same fall + paddle-catch loop as power-ups
      // but they top up the in-run wallet instead of triggering an effect.
      if (endless && s.moonstones && s.moonstones.length) {
        const perks = walletCfgRef.current.perks || [];
        const magnetActive = perks.includes("lunar-magnet");
        const magnetR = 120;
        const paddleCx = s.paddle.x + s.paddle.w / 2;
        for (let i = s.moonstones.length - 1; i >= 0; i--) {
          const m = s.moonstones[i];
          // Lunar Magnet: gently steer the stone horizontally toward the
          // paddle once it enters the magnet radius. Speed is capped so it
          // doesn't snap unrealistically — adds a 0.6px/frame nudge.
          if (magnetActive) {
            const dx = paddleCx - m.x;
            if (Math.abs(dx) < magnetR) {
              const dir = dx === 0 ? 0 : dx / Math.abs(dx);
              m.x += dir * Math.min(Math.abs(dx) * 0.05, 0.6) * (dt / 16);
            }
          }
          // Magnet Paddle artifact: stronger, level-scaled pull on top.
          artifactPowers.applyMagnetPull(artifactsRef.current, m, paddleCx, dt);
          m.y += m.vy * (dt / 16);
          const paddleHit =
            m.y + m.r >= PADDLE_Y &&
            m.y - m.r <= PADDLE_Y + PADDLE_H &&
            m.x >= s.paddle.x &&
            m.x <= s.paddle.x + s.paddle.w;
          if (paddleHit) {
            spawnParticles(m.x, PADDLE_Y - 6, getRenderPalette()?.mid || "#c084fc");
            // Lunar Streak — catching 3 stones within 4s arms a +2-stones
            // bonus window for the next 5 catches. Stones beyond the cap
            // are still absorbed but the bonus still ticks down so the
            // streak doesn't exploit a full wallet.
            const tNow = gameNow();
            const streak = stoneStreakRef.current;
            // Personal-best chain — increment if the previous catch was
            // within 4s of this one, otherwise reset to 1.
            const lastTs = streak.timestamps.length ? streak.timestamps[streak.timestamps.length - 1] : -Infinity;
            if (tNow - lastTs < 4000) streak.currentChain += 1;
            else streak.currentChain = 1;
            if (streak.currentChain > streak.bestChainThisRun) {
              streak.bestChainThisRun = streak.currentChain;
            }
            streak.timestamps = streak.timestamps.filter((ts) => tNow - ts < 4000);
            streak.timestamps.push(tNow);
            let amount = 1;
            let gold = false;
            if (streak.bonusLeft > 0) {
              amount = 2;
              gold = true;
              streak.bonusLeft -= 1;
            } else if (streak.timestamps.length >= 3) {
              // Trigger the bonus on the qualifying catch. Reset window so
              // the next 3-in-4s requires another fresh streak after the
              // bonus runs out.
              streak.bonusLeft = 4; // 4 more @ 2x after this gold tick
              amount = 2;
              gold = true;
              streak.timestamps = [];
            }
            // Wallet is hard-capped; addition past cap is absorbed.
            setMoonstones((c) => Math.min(walletCfgRef.current.cap || 5000, c + amount));
            stonesCollectedRef.current += amount;
            // Surface the "First Moonstone" tip exactly once per pilot
            // (localStorage-gated). Only in Endless (the only mode where
            // moonstones are routinely earned) and only on md+ viewports
            // so the banner doesn't cover half the play-field on mobile.
            try {
              if (
                endless
                && typeof window !== "undefined"
                && window.innerWidth >= 768
                && localStorage.getItem("cosmic_moonstone_tip_seen") !== "1"
              ) {
                setShowMoonstoneTip(true);
              }
            } catch {
              // privacy mode / quota — silently skip; not critical.
            }
            // Spawn a floating "+N ☾" tick anchored above the paddle. Gold
            // when streak-doubled, palette-coloured otherwise.
            s.stoneTicks = s.stoneTicks || [];
            s.stoneTicks.push({ x: m.x, y: PADDLE_Y - 8, t0: tNow, amount, gold });
            // Persist the FULL amount (1 or 2) immediately to the wallet.
            apiClient.post("/moonstones/adjust", { delta: amount }).catch(() => {});
            Sounds.powerUp();
            s.moonstones.splice(i, 1);
          } else if (m.y > H + 30) {
            s.moonstones.splice(i, 1);
          }
        }
      }

      // Endless: Plasma Shot — paddle auto-fires plasma bolts while
      // the timer is active. Cadence + bolt count come from
      // `plasmaFireInterval` / `plasmaBoltsPerFire` on the fx ref so
      // boosted milestone fires can paint a Vulcan-cannon spread.
      if (endless) {
        const fx = moonstoneFxRef.current;
        const now = gameNow();
        // Drain the boosted-plasma activation ring. Painted once per
        // milestone fire to highlight the new Vulcan cannon mount.
        if (boostedPlasmaActivationAt.current > 0) {
          const cx = s.paddle.x + s.paddle.w / 2;
          for (let k = 0; k < 18; k++) {
            spawnParticles(
              cx + Math.cos(k * 0.35) * 24,
              PADDLE_Y + Math.sin(k * 0.35) * 8,
              "#ff66ff",
            );
          }
          boostedPlasmaActivationAt.current = 0;
        }
        if (fx.plasmaUntil > now && now >= fx.plasmaNextFireAt) {
          s.plasmaBolts = s.plasmaBolts || [];
          const px = s.paddle.x + s.paddle.w / 2;
          const py = PADDLE_Y - 4;
          const bolts = Math.max(1, Number(fx.plasmaBoltsPerFire || 1));
          if (bolts === 1) {
            s.plasmaBolts.push({ x: px, y: py, vy: -14, vx: 0 });
          } else {
            // 3-bolt fan: left/center/right with a slight horizontal
            // velocity for the wing bolts so they sweep outward as
            // they climb. Spread is gentle (±2.6 vx) so all three
            // typically hit a brick row from one side to the other.
            s.plasmaBolts.push({ x: px - 14, y: py, vy: -14, vx: -2.6 });
            s.plasmaBolts.push({ x: px,      y: py, vy: -14, vx:  0 });
            s.plasmaBolts.push({ x: px + 14, y: py, vy: -14, vx:  2.6 });
          }
          const interval = Math.max(120, Number(fx.plasmaFireInterval || 1000));
          setMoonstoneFx((cur) => ({ ...cur, plasmaNextFireAt: now + interval }));
          Sounds.bouncePaddle();
        }
        if (s.plasmaBolts && s.plasmaBolts.length) {
          for (let i = s.plasmaBolts.length - 1; i >= 0; i--) {
            const p = s.plasmaBolts[i];
            p.y += p.vy * (dt / 16);
            // Horizontal drift for spread bolts (vx is 0 for vertical).
            if (p.vx) p.x += p.vx * (dt / 16);
            if (p.y < -10 || p.x < -10 || p.x > W + 10) { s.plasmaBolts.splice(i, 1); continue; }
            // Collide with first overlapping block (any tier).
            for (let j = s.blocks.length - 1; j >= 0; j--) {
              const b = s.blocks[j];
              if (p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h) {
                const color = TIER_COLORS[b.tier];
                spawnParticles(b.x + b.w / 2, b.y + b.h / 2, color);
                spawnPowerup(b.x + b.w / 2, b.y + b.h / 2);
                spawnMoonstone(b.x + b.w / 2, b.y + b.h / 2);
                s.blocks.splice(j, 1);
                bumpCombo(1);
                setScore((sc) => sc + Math.round(50 * b.tier * comboMultiplier(comboRef.current.count)));
                if (metricsRef.current) {
                  metricsRef.current.blocksDestroyed += 1;
                  if (b.tier === 4) metricsRef.current.bossKilled = true;
                }
                Sounds.destroyBlock();
                s.plasmaBolts.splice(i, 1);
                break;
              }
            }
          }
        }
        // [worm-swarm update lives outside the endless gate — see below]
      }

      // --- Blood-Red Stingray Swarm -----------------------------------
      // Independent of the endless gate — the `stingray` power-up drops
      // in any mode. Each stingray homes in on the nearest boss target
      // (priority: spaceWorm → rareWorm → tier-4 brick → any brick).
      // Once close enough, deal 2 damage and trigger a 250ms self-
      // cooldown so a single stingray can't shred a Leviathan in one
      // frame. Expires after `life` ms or when no targets remain.
      if (s.stingrays && s.stingrays.length) {
        const now = performance.now();
        for (let i = s.stingrays.length - 1; i >= 0; i--) {
          const r = s.stingrays[i];
          // Lifetime expiry.
          if (now - r.bornAt > r.life) {
            spawnParticles(r.x, r.y, "#ff1f3a");
            s.stingrays.splice(i, 1);
            continue;
          }
          // Pick the highest-priority target each frame.
          let tx = null, ty = null, kind = null;
          if (s.spaceWorms && s.spaceWorms.length) {
            // Prefer non-skeleton (Leviathan) targets over skeletons.
            let best = null;
            let bestD = Infinity;
            for (const sw of s.spaceWorms) {
              if (sw.hp <= 0) continue;
              const d = Math.hypot(sw.x - r.x, sw.y - r.y)
                + (sw.kind === "skeleton" ? 80 : 0); // skeletons deprioritised
              if (d < bestD) { bestD = d; best = sw; }
            }
            if (best) {
              tx = best.x; ty = best.y; kind = "spaceWorm";
              r._targetRef = best;
            }
          }
          if (tx === null && s.rareWorm && s.rareWorm.hp > 0 && s.rareWorm.phase === "idle") {
            tx = s.rareWorm.x; ty = s.rareWorm.y; kind = "rareWorm";
          }
          if (tx === null) {
            // Fall back to nearest tier-4 (boss) brick, else nearest brick.
            let best = null;
            let bestD = Infinity;
            const candidates = s.blocks.filter((b) => b.tier === 4);
            const pool = candidates.length ? candidates : s.blocks;
            for (const b of pool) {
              const bx = b.x + b.w / 2;
              const by = b.y + b.h / 2;
              const d = Math.hypot(bx - r.x, by - r.y);
              if (d < bestD) { bestD = d; best = b; }
            }
            if (best) { tx = best.x + best.w / 2; ty = best.y + best.h / 2; kind = "brick"; }
          }
          // Steer toward target — gentle homing so the path reads as a
          // hunting manta-ray rather than a homing missile.
          if (tx !== null) {
            const dx = tx - r.x;
            const dy = ty - r.y;
            const dlen = Math.hypot(dx, dy) || 1;
            const steerStrength = 0.25; // 0..1 — higher = sharper turns
            const targetVx = (dx / dlen) * 4.6;
            const targetVy = (dy / dlen) * 4.6;
            r.vx = r.vx * (1 - steerStrength) + targetVx * steerStrength;
            r.vy = r.vy * (1 - steerStrength) + targetVy * steerStrength;
          }
          // Speed cap so the stingray stays readable.
          const maxSpd = 4.8;
          const sp = Math.hypot(r.vx, r.vy) || 1;
          if (sp > maxSpd) { r.vx = (r.vx / sp) * maxSpd; r.vy = (r.vy / sp) * maxSpd; }
          r.x += r.vx * (dt / 16);
          r.y += r.vy * (dt / 16);
          r.wingPhase += dt * 0.012; // wing flap
          // Edge wrap — stingrays don't bounce, they're ghosts.
          if (r.x < -20) r.x = W + 20;
          if (r.x > W + 20) r.x = -20;
          if (r.y < GRID_TOP - 20) r.y = GRID_TOP - 20;
          if (r.y > H + 20) r.y = H + 20;

          // Damage check — only fires when cooldown elapsed.
          if (now >= r.dmgCooldownUntil) {
            const hitR = 26;
            // vs spaceWorms
            if (s.spaceWorms && s.spaceWorms.length) {
              for (let wi = s.spaceWorms.length - 1; wi >= 0; wi--) {
                const sw = s.spaceWorms[wi];
                const bodyR = sw.kind === "giant" ? 56 : sw.kind === "skeleton" ? 22 : 32;
                if (Math.hypot(sw.x - r.x, sw.y - r.y) < bodyR + hitR * 0.4) {
                  sw.hp -= 2;
                  sw.hitFlash = 12;
                  spawnParticles(sw.x, sw.y, "#ff1f3a");
                  spawnParticles(sw.x, sw.y, "#ff5577");
                  Sounds.hitBlock?.();
                  r.dmgCooldownUntil = now + 250;
                  // Big death burst handled by the existing ball-vs-
                  // spaceWorm kill path on its next overlap — but if
                  // the stingray itself lands the killing blow, mark
                  // hp at 0 and let the kill handler downstream
                  // process the removal. To keep behavior consistent
                  // with the ball kill path, we simulate a tiny score
                  // bump and remove the worm here on kill.
                  if (sw.hp <= 0) {
                    for (let bi = 0; bi < 6; bi++) {
                      spawnParticles(sw.x + (Math.random() - 0.5) * bodyR, sw.y, bi % 2 ? "#ff1f3a" : "#ffd700");
                    }
                    // BLOOD SPLATTER — Leviathans get a full splat, skeletons
                    // get a tighter half-size spray so fragile mobs don't
                    // drown the screen in red. Giants get a double splat.
                    if (sw.kind === "skeleton") {
                      const splatsBefore = s.bloodSplats.length;
                      spawnBloodSplats(sw.x, sw.y);
                      for (let bi = splatsBefore; bi < s.bloodSplats.length; bi++) {
                        s.bloodSplats[bi].r *= 0.55;
                      }
                    } else {
                      spawnBloodSplats(sw.x, sw.y);
                      if (sw.kind === "giant") spawnBloodSplats(sw.x, sw.y);
                      // CARNAGE COUNTER — Leviathan-only kill credit.
                      // Fire-and-forget; surfaces tier-unlock toasts on
                      // 10 / 50 / 200 kill milestones.
                      const wv = endless ? endlessWave : 0;
                      apiClient
                        .post("/endless/stingray-kill", { wave: wv })
                        .then((rsp) => {
                          const awarded = rsp?.data?.newly_awarded || [];
                          // Update the local carnage state so the
                          // sidebar chip refreshes without a refetch.
                          if (typeof rsp?.data?.count === "number") {
                            setCarnageCount(rsp.data.count);
                            setCarnageNextTier(rsp.data.next_tier || null);
                          }
                          for (const sk of awarded) {
                            toast.success(
                              `${(sk.name || "Carnage").toUpperCase()} · Tier unlocked`,
                              {
                                description: `${rsp.data.count} stingray boss kills — equip your new trophy in /skins.`,
                                icon: <Sparkles className="w-4 h-4" style={{ color: "#ff1f3a" }} />,
                                duration: 7000,
                              },
                            );
                          }
                        })
                        .catch(() => {});
                    }
                    const bonus = sw.kind === "giant" ? 2500 : sw.kind === "skeleton" ? 400 : 1000;
                    setScore((sc) => sc + bonus);
                    if (metricsRef.current) {
                      metricsRef.current.blocksDestroyed += 1;
                      if (sw.kind !== "skeleton") metricsRef.current.bossKilled = true;
                    }
                    Sounds.destroyBlock?.();
                    s.spaceWorms.splice(wi, 1);
                  }
                  break;
                }
              }
            }
            // vs rareWorm (Wormhole)
            if (s.rareWorm && s.rareWorm.hp > 0 && s.rareWorm.phase === "idle") {
              const rw = s.rareWorm;
              if (Math.hypot(rw.x - r.x, rw.y - r.y) < 36 + hitR * 0.3) {
                rw.hp -= 2;
                rw.hitFlash = 12;
                spawnParticles(rw.x, rw.y, "#ff1f3a");
                r.dmgCooldownUntil = now + 250;
                if (rw.hp <= 0) {
                  spawnParticles(rw.x, rw.y, "#ff00ff");
                  spawnParticles(rw.x, rw.y, "#ffd700");
                  // BLOOD SPLATTER — stingray landed the killing blow
                  // on the rare Wormhole Worm.
                  spawnBloodSplats(rw.x, rw.y);
                  setScore((sc) => sc + 2500);
                  s.rareWorm = null;
                }
              }
            }
            // vs blocks — only if no worm targets exist or to clean up
            // along the path. Damages 1 HP per touch (matches ball).
            if (now >= r.dmgCooldownUntil && s.blocks.length) {
              for (let j = s.blocks.length - 1; j >= 0; j--) {
                const b = s.blocks[j];
                if (
                  r.x >= b.x - 4 && r.x <= b.x + b.w + 4
                  && r.y >= b.y - 4 && r.y <= b.y + b.h + 4
                ) {
                  b.hp -= 1;
                  if (b.hp <= 0) {
                    const color = TIER_COLORS[b.tier];
                    spawnParticles(b.x + b.w / 2, b.y + b.h / 2, color);
                    spawnParticles(b.x + b.w / 2, b.y + b.h / 2, "#ff1f3a");
                    s.blocks.splice(j, 1);
                    setScore((sc) => sc + Math.round(50 * b.tier));
                    if (metricsRef.current) {
                      metricsRef.current.blocksDestroyed += 1;
                      if (b.tier === 4) metricsRef.current.bossKilled = true;
                    }
                    Sounds.destroyBlock?.();
                  } else {
                    Sounds.hitBlock?.();
                  }
                  r.dmgCooldownUntil = now + 250;
                  break;
                }
              }
            }
          }
        }
      }

      // --- Laser Paddle artifact ----------------------------------------
      // Fires twin lasers from the paddle edges on a cooldown derived
      // from the artifact's current tier (10s → 3s). Each bolt
      // destroys the first brick it touches. Works in any non-
      // competitive mode (not just endless).
      {
        const aState = artifactsRef.current;
        if (
          aState && !aState.competitive &&
          aState.paddle?.power_kind === "laser"
        ) {
          const cooldownMs =
            artifactPowers.paddleValue(aState) * 1000;
          const nowT = performance.now();
          if (!aState.laserNextFireAt || nowT >= aState.laserNextFireAt) {
            s.laserBolts = s.laserBolts || [];
            const cx = s.paddle.x + s.paddle.w / 2;
            const halfGap = Math.max(18, s.paddle.w * 0.4);
            s.laserBolts.push({ x: cx - halfGap, y: PADDLE_Y - 4, vy: -16 });
            s.laserBolts.push({ x: cx + halfGap, y: PADDLE_Y - 4, vy: -16 });
            aState.laserNextFireAt = nowT + cooldownMs;
            Sounds.powerUp();
            // Brief paddle muzzle flash (cyan).
            spawnParticles(cx - halfGap, PADDLE_Y - 6, "#7df9ff");
            spawnParticles(cx + halfGap, PADDLE_Y - 6, "#7df9ff");
          }
        }
        if (s.laserBolts && s.laserBolts.length) {
          for (let i = s.laserBolts.length - 1; i >= 0; i--) {
            const p = s.laserBolts[i];
            p.y += p.vy * (dt / 16);
            if (p.y < -10) { s.laserBolts.splice(i, 1); continue; }
            for (let j = s.blocks.length - 1; j >= 0; j--) {
              const b = s.blocks[j];
              if (
                p.x >= b.x && p.x <= b.x + b.w &&
                p.y >= b.y && p.y <= b.y + b.h
              ) {
                const color = TIER_COLORS[b.tier];
                spawnParticles(b.x + b.w / 2, b.y + b.h / 2, color);
                spawnMoonstone(b.x + b.w / 2, b.y + b.h / 2);
                s.blocks.splice(j, 1);
                setScore((sc) => sc + Math.round(50 * b.tier));
                if (metricsRef.current) metricsRef.current.blocksDestroyed += 1;
                Sounds.destroyBlock();
                s.laserBolts.splice(i, 1);
                break;
              }
            }
          }
        }
      }
      // --- end Laser Paddle artifact -----------------------------------
      // Worm Swarm — manually-launched green worms that hunt the
      // nearest brick, deal 1 dmg on contact, then repath. Each worm
      // dies after `killsLeft` reaches 0 OR `expiresAt` is exceeded.
      // Wobble adds personality without breaking the homing path.
      // Lives OUTSIDE the `if (endless)` block because the Worm Swarm
      // starter is purchasable and launchable in BOTH regular play and
      // Endless Mode.
      if (s.wormSwarms && s.wormSwarms.length) {
        const nowTs = performance.now();
        const speed = 3.2;
        for (let i = s.wormSwarms.length - 1; i >= 0; i--) {
          const w = s.wormSwarms[i];
          // Expire on lifetime or kill exhaustion.
          if (nowTs > w.expiresAt || w.killsLeft <= 0) {
            s.wormSwarms.splice(i, 1);
            continue;
          }
          // Find nearest brick if no target / target gone.
          let target = w.targetIdx >= 0 ? s.blocks[w.targetIdx] : null;
          if (!target) {
            let bestIdx = -1, bestDist = Infinity;
            for (let j = 0; j < s.blocks.length; j++) {
              const b = s.blocks[j];
              const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
              const dx = cx - w.x, dy = cy - w.y;
              const d = dx * dx + dy * dy;
              if (d < bestDist) { bestDist = d; bestIdx = j; }
            }
            w.targetIdx = bestIdx;
            target = bestIdx >= 0 ? s.blocks[bestIdx] : null;
          }
          // No bricks left → drift until lifetime expires.
          if (!target) {
            w.x += w.vx * (dt / 16);
            w.y += w.vy * (dt / 16);
          } else {
            const tcx = target.x + target.w / 2;
            const tcy = target.y + target.h / 2;
            const dx = tcx - w.x, dy = tcy - w.y;
            const dist = Math.hypot(dx, dy) || 1;
            // Wobble — perpendicular to heading, time-modulated. Adds the
            // characteristic worm-like wriggle without breaking aim.
            const wobble = Math.sin(nowTs * 0.012 + w.seed) * 0.6;
            w.vx = (dx / dist) * speed + (-dy / dist) * wobble;
            w.vy = (dy / dist) * speed + (dx / dist) * wobble;
            w.x += w.vx * (dt / 16);
            w.y += w.vy * (dt / 16);
            // Hit-test the target.
            if (
              w.x >= target.x && w.x <= target.x + target.w &&
              w.y >= target.y && w.y <= target.y + target.h
            ) {
              target.hp = Math.max(0, target.hp - 1);
              if (target.hp <= 0) {
                const color = TIER_COLORS[target.tier];
                spawnParticles(tcx, tcy, color);
                spawnParticles(tcx, tcy, "#00ff66");
                spawnPowerup(tcx, tcy);
                spawnMoonstone(tcx, tcy);
                s.blocks.splice(w.targetIdx, 1);
                bumpCombo(1);
                setScore((sc) => sc + Math.round(50 * target.tier * comboMultiplier(comboRef.current.count)));
                if (metricsRef.current) {
                  metricsRef.current.blocksDestroyed += 1;
                  if (target.tier === 4) metricsRef.current.bossKilled = true;
                }
                // Worm-credit counter — flushed to /achievements/worm-kills
                // in batches so the Worm Tamer (100-kills) achievement
                // unlocks. Local accumulator avoids spamming the network
                // mid-swarm; flushed by the wormSwarms-empty fence below.
                wormKillsRef.current += 1;
                Sounds.destroyBlock();
              } else {
                setScore((sc) => sc + 10);
                Sounds.hitBlock();
              }
              // Pop kill counter, reset target so it re-picks the next
              // closest brick on the next frame, and slightly randomise
              // the heading so a swarm of 5 doesn't all dogpile one row.
              w.killsLeft -= 1;
              w.targetIdx = -1;
              w.x += (Math.random() - 0.5) * 12;
              w.y += (Math.random() - 0.5) * 12;
            }
          }
          // Push current head into segment trail (keep last 6 positions).
          w.segments.unshift({ x: w.x, y: w.y });
          if (w.segments.length > 6) w.segments.length = 6;
        }
        // Flush accumulated worm kills the moment the swarm fully expires.
        // Batched to avoid one network call per brick — a typical swarm
        // produces ~15 kills which we send in a single POST. Cap at the
        // server-side ceiling (30) and slice off any spillover so a
        // pathological run never trips the validator.
        if (s.wormSwarms.length === 0 && wormKillsRef.current > 0) {
          const flush = Math.min(30, wormKillsRef.current);
          wormKillsRef.current -= flush;
          apiClient
            .post("/achievements/worm-kills", { count: flush })
            .then((r) => {
              const newly = r.data?.newly_unlocked_achievements || [];
              for (const a of newly) {
                toast.success(`${a.name}`, {
                  description: `${a.desc} · +${(a.reward_score || 0).toLocaleString()} pts`,
                  duration: 5000,
                });
                // Worm Tamer just unlocked — flip the cosmetic flag so the
                // next swarm renders in the gold-green Mass Extinction
                // palette without waiting for a fresh /moonstones/me load.
                if (a.id === "worm-tamer") {
                  setWalletCfg((c) => ({ ...c, wormTamerUnlocked: true }));
                  toast("Mass Extinction skin unlocked!", {
                    description: "Future Worm Swarms shimmer gold.",
                    duration: 6000,
                  });
                }
              }
            })
            .catch(() => {});
        }
      }

      for (const b of s.blocks) b.rot += b.rotSpeed * (dt / 16);

      // RARE WORMHOLE WORM — Endless-only collectible. Teleports
      // through wormhole portals every 4-7s and occasionally fires a
      // slow plasma bolt at the paddle. State machine:
      //   idle → fade-out → port-out → relocate → port-in → fade-in → idle.
      // The ball can only damage the worm during `idle` and `fade-in`
      // phases (when it's solid). During `port-out`/`port-in` the worm
      // is intangible.
      if (s.rareWorm) {
        const rw = s.rareWorm;
        const now = gameNow();
        rw.portalT += dt / 600;
        if (rw.hitFlash > 0) rw.hitFlash -= 1;
        if (rw.phase === "idle") {
          // Subtle hover wiggle.
          rw.x += Math.sin(now / 700) * 0.4;
          rw.y += Math.cos(now / 900) * 0.25;
          // Trigger a teleport when timer expires.
          if (now >= rw.nextTeleportAt) {
            rw.phase = "fade-out";
            rw.phaseUntil = now + 400;
          }
          // Fire a plasma bolt at the paddle when the cooldown expires.
          if (now >= rw.nextFireAt) {
            const px = stateRef.current.paddle.x
              + stateRef.current.paddle.w / 2;
            const py = H - 30;
            const dx0 = px - rw.x;
            const dy0 = py - rw.y;
            const len = Math.hypot(dx0, dy0) || 1;
            const sp = 2.4; // slow & dodgeable
            s.wormBolts.push({
              x: rw.x,
              y: rw.y,
              vx: (dx0 / len) * sp,
              vy: (dy0 / len) * sp,
              bornAt: now,
            });
            Sounds.powerUp?.();
            rw.nextFireAt = now + 4500 + Math.random() * 2000;
          }
        } else if (rw.phase === "fade-out") {
          if (now >= rw.phaseUntil) {
            rw.phase = "port-out";
            rw.phaseUntil = now + 300;
          }
        } else if (rw.phase === "port-out") {
          if (now >= rw.phaseUntil) {
            // Relocate: random new screen position within play bounds.
            // Stay clear of the paddle's hitbox.
            rw.x = 80 + Math.random() * (W - 160);
            rw.y = GRID_TOP + 40 + Math.random() * Math.max(60, H * 0.45);
            // Spawn a tiny portal-burst at the new location.
            spawnParticles(rw.x, rw.y, "#ff00ff");
            spawnParticles(rw.x, rw.y, "#3a0a66");
            Sounds.warp?.();
            rw.phase = "port-in";
            rw.phaseUntil = now + 300;
          }
        } else if (rw.phase === "port-in") {
          if (now >= rw.phaseUntil) {
            rw.phase = "fade-in";
            rw.phaseUntil = now + 400;
          }
        } else if (rw.phase === "fade-in") {
          if (now >= rw.phaseUntil) {
            rw.phase = "idle";
            rw.nextTeleportAt = now + 4500 + Math.random() * 2500;
          }
        }
      }

      // Worm-fired plasma bolt updates. Move each bolt down toward
      // the paddle; if it leaves the play area, drop it. If it hits
      // the paddle, lose a life (same path as ball-fall-through).
      if (s.wormBolts && s.wormBolts.length) {
        const now2 = gameNow();
        const paddle = s.paddle;
        for (let bi = s.wormBolts.length - 1; bi >= 0; bi--) {
          const bolt = s.wormBolts[bi];
          bolt.x += bolt.vx;
          bolt.y += bolt.vy;
          // Off-screen (any edge) — drop.
          if (bolt.x < -20 || bolt.x > W + 20 || bolt.y < -20 || bolt.y > H + 20) {
            s.wormBolts.splice(bi, 1);
            continue;
          }
          // Lifetime cap (5s) — safety net.
          if (now2 - bolt.bornAt > 5000) {
            s.wormBolts.splice(bi, 1);
            continue;
          }
          // Paddle collision — circle vs rect.
          const py = H - 30;
          const closestX = Math.max(paddle.x, Math.min(bolt.x, paddle.x + paddle.w));
          const closestY = Math.max(py, Math.min(bolt.y, py + 14));
          const ddx = bolt.x - closestX;
          const ddy = bolt.y - closestY;
          if (ddx * ddx + ddy * ddy < 12 * 12) {
            s.wormBolts.splice(bi, 1);
            spawnParticles(bolt.x, bolt.y, "#ff00ff");
            // Lose a life — same path as ball-fall-through.
            livesRef.current = Math.max(0, livesRef.current - 1);
            setLives(livesRef.current);
            Sounds.die?.();
            if (livesRef.current <= 0) {
              setGameOver(true);
            }
          }
        }
      }

      // Space Worm movement — smooth wandering across the entire canvas.
      // We use a Lissajous-like sine path biased by `vx/vy` for forward
      // momentum, then reflect off walls so the worm never escapes the
      // play area. `wanderT` advances every frame to vary the heading,
      // creating organic motion without expensive noise generation.
      // Tail trail captured each frame for the segmented sway.
      if (s.spaceWorms && s.spaceWorms.length) {
        const phaseActive = endless && moonstoneFxRef.current.phaseUntil > gameNow();
        for (const sw of s.spaceWorms) {
          sw.wanderT += dt / 1000;
          // Steering: a slowly-rotating direction vector with a small
          // perpendicular jitter so the path snakes naturally.
          const driftAng = Math.sin(sw.wanderT * 0.5) * 0.018;
          const cs = Math.cos(driftAng), sn = Math.sin(driftAng);
          const nvx = sw.vx * cs - sw.vy * sn;
          const nvy = sw.vx * sn + sw.vy * cs;
          sw.vx = nvx;
          sw.vy = nvy + Math.sin(sw.wanderT * 1.7) * 0.04;
          // Speed cap so the worm doesn't accelerate forever.
          // RAGE MODE: non-skeleton Leviathans below 30% HP move 1.45×
          // faster — gives the RAGE banner real teeth without changing
          // attack patterns (still pure ball-vs-body combat).
          const ragingLeviathan =
            sw.kind !== "skeleton"
            && sw.maxHp
            && sw.hp > 0
            && sw.hp / sw.maxHp < 0.3;
          const rageMult = ragingLeviathan ? 1.45 : 1.0;
          const maxSpd = (sw.kind === "giant"
            ? 1.6
            : sw.kind === "skeleton"
              ? 2.8 // skeletons are nimble — fastest of the three
              : 2.4) * rageMult;
          const sp = Math.hypot(sw.vx, sw.vy) || 1;
          if (sp > maxSpd) {
            sw.vx = (sw.vx / sp) * maxSpd;
            sw.vy = (sw.vy / sp) * maxSpd;
          }
          sw.x += sw.vx * (dt / 16);
          sw.y += sw.vy * (dt / 16);
          // Edge bounce — keep clear of paddle area at the bottom and the
          // HUD area at the top. Reflect with a small damping so motion
          // stays interesting.
          const bodyR = sw.kind === "giant" ? 60 : sw.kind === "skeleton" ? 26 : 36;
          const minY = GRID_TOP + bodyR * 0.6;
          const maxY = PADDLE_Y - bodyR - 20;
          if (sw.x < bodyR) { sw.x = bodyR; sw.vx = Math.abs(sw.vx); }
          if (sw.x > W - bodyR) { sw.x = W - bodyR; sw.vx = -Math.abs(sw.vx); }
          if (sw.y < minY) { sw.y = minY; sw.vy = Math.abs(sw.vy); }
          if (sw.y > maxY) { sw.y = maxY; sw.vy = -Math.abs(sw.vy); }
          // Head phase drives subtle body sway in the draw step.
          sw.headPhase += dt * 0.004;
          if (sw.hitFlash > 0) sw.hitFlash -= 1;
          // Push current position into the segment trail for body sway.
          sw.segments.unshift({ x: sw.x, y: sw.y });
          const maxSeg = sw.kind === "giant" ? 18 : sw.kind === "skeleton" ? 8 : 12;
          if (sw.segments.length > maxSeg) sw.segments.length = maxSeg;
          // Avoid lint warning when phase var isn't used here.
          void phaseActive;
        }
      }

      if (s.blocks.length === 0 && (s.spaceWorms?.length ?? 0) === 0 && !won && !gameOver && !waveClearedRef.current) {
        waveClearedRef.current = true;
        if (endless) {
          // Wave-clear bonus: +500 pts for clearing the wave so progressive
          // pilots accumulate score even on quick waves. Plays the standard
          // win chime briefly, but does NOT stop music — the run continues.
          setScore((sc) => sc + 500);
          Sounds.win();
          // BOOST STREAK — milestone waves (every 5th) compound a flat
          // bonus on clear: 1000 / 1500 / 2000 / 2500 / 3000 at streaks
          // 1-5+. Caps at 5. Resets on life loss (handled in the death
          // handler). Toast surfaces the streak + the bonus so the
          // pilot reads it as a deliberate reward beat.
          const justCleared = endlessWave;
          if (justCleared > 0 && justCleared % 5 === 0) {
            const bs = boostStreakRef.current;
            bs.count = Math.min(5, bs.count + 1);
            if (bs.count > bs.best) bs.best = bs.count;
            const bonus = 1000 + (bs.count - 1) * 500;
            setScore((sc) => sc + bonus);
            toast.success(`BOOST STREAK ×${bs.count} · +${bonus} pts`, {
              description:
                bs.count >= 5
                  ? "Max streak — keep it alive."
                  : `Chain the next milestone for a bigger bonus.`,
              icon: <Sparkles className="w-4 h-4 text-[#ffd700]" />,
              duration: 2500,
            });
            if (metricsRef.current) {
              metricsRef.current.best_boost_streak = Math.max(
                metricsRef.current.best_boost_streak || 0,
                bs.best,
              );
            }
            // FLAWLESS HUNTER — fire the trophy claim the moment both
            // gates are crossed in the same run (wave>=30 cleared AND
            // best streak >=5). Once-per-run guard via a local ref so
            // the claim only fires once even though the conditions
            // stay true for every subsequent milestone clear.
            if (
              bs.best >= 5 &&
              justCleared >= 30 &&
              !flawlessClaimedThisRunRef.current
            ) {
              flawlessClaimedThisRunRef.current = true;
              apiClient
                .post("/endless/flawless-hunter-claim", {
                  wave: justCleared,
                  best_boost_streak: bs.best,
                })
                .then((r) => {
                  if (r?.data?.awarded) {
                    toast.success("FLAWLESS HUNTER · Marathon-gold skin unlocked", {
                      description: "Equip from the Skins menu — gold + cyan + void palette.",
                      icon: <Sparkles className="w-4 h-4 text-[#ffd700]" />,
                      duration: 8000,
                    });
                  }
                })
                .catch(() => {
                  // Already-owned / network blip — silent.
                });
            }
          }
          if (onWaveClear) onWaveClear(boostStreakRef.current.count);
          // Persist a server-side checkpoint of the current run so
          // a crash / page-reload / device switch can be recovered.
          // Fire-and-forget — failure here must NEVER block gameplay.
          // The next wave's clear will overwrite this anyway.
          //
          // We snapshot `score` via the functional setScore form so we
          // capture the latest value (the wave-clear bonus + boost
          // streak bonus were just applied via setScore() calls above).
          try {
            setScore((curScore) => {
              const snap = Math.round(curScore || 0);
              apiClient.post("/endless/checkpoint", {
                wave: justCleared + 1, // wave the pilot would resume INTO
                score: snap,
                // Lives ref is kept in sync with the lives state via
                // the dedicated useEffect at line ~126, so this is
                // always the latest count even mid-frame.
                lives: Math.max(1, Math.min(9, livesRef.current || 3)),
              })
                .then(() => {
                  // Surface a brief gold "AUTO-SAVED" pill so the pilot
                  // gets a visible trust signal that their progress is
                  // safely persisted. Pill auto-fades after 1.6s via
                  // the consumer's transition logic.
                  setAutoSavedAt(Date.now());
                })
                .catch(() => {});
              return curScore; // setScore is just being used as a reader
            });
          } catch {
            // Defensive — if apiClient ever throws synchronously, ignore.
          }
        } else {
          setWon(true);
          Sounds.win();
          stopMusic();
          // Boss-defeat camera-shake — tiered by how decisively the boss
          // died. Reads `metricsRef.current.startedAt` (set at level mount
          // for non-endless runs) so a 27s blitz registers as a "crushing
          // victory" while a 70s grind reads as "hard fought". Reuses the
          // same cameraShakeRef helper applied each frame to the canvas
          // DOM transform.
          // NOTE: read `levelCfg.boss` directly here. The `isBoss` const
          // we used previously was scoped to `buildBlocks` and crashed
          // the rAF loop on every-10/20 worm levels with a ReferenceError.
          if (levelCfg && levelCfg.boss) {
            const m = metricsRef.current;
            const dur = m && m.startedAt ? performance.now() - m.startedAt : 0;
            let magnitude = 8;
            let lifeMs = 250;
            if (dur > 0 && dur < 30000) {
              magnitude = 12;
              lifeMs = 350;
            } else if (dur > 60000) {
              magnitude = 5;
              lifeMs = 200;
            }
            cameraShakeRef.current = { until: gameNow() + lifeMs, magnitude };
          }
        }
      }

      renderWorldWithZoom(s);
      // Camera-shake — when active, jitter the canvas element via CSS
      // transform on the DOM node directly (no React state). Decays
      // linearly to zero at `until`. We cap on a tiny dead-zone to avoid
      // sub-pixel jitter once the shake is essentially done.
      if (canvasRef.current) {
        const sk = cameraShakeRef.current;
        const remaining = sk.until - gameNow();
        if (remaining > 0 && sk.magnitude > 0) {
          const decay = Math.max(0, remaining / 200);
          const m = sk.magnitude * decay;
          const dx = (Math.random() - 0.5) * 2 * m;
          const dy = (Math.random() - 0.5) * 2 * m;
          canvasRef.current.style.transform = `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`;
        } else if (canvasRef.current.style.transform) {
          canvasRef.current.style.transform = "";
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused, gameOver, won, levelCfg, activeSkin, paddleAccent, ballSkin, endless, onWaveClear]);

  // Report completion to parent. Normal mode → fires once on `won`.
  // Endless mode → fires once on `gameOver` (run ends only on losing all
  // lives; wave clears are signalled via `onWaveClear` instead).
  useEffect(() => {
    if (won && !endless) {
      const m = metricsRef.current || {};
      const durationMs = m.startedAt ? Math.round(performance.now() - m.startedAt) : null;
      onComplete?.(score, {
        duration_ms: durationMs,
        perfect: !m.lostLife,
        max_balls: m.maxBalls || 1,
        powerups_collected: m.powerupsCollected || 0,
        blocks_destroyed: m.blocksDestroyed || 0,
        boss_killed: !!m.bossKilled,
        boss_rank: m.bossRank,
        best_combo: m.bestCombo || 0,
        // Shape Master tracking — server credits each canonical shape id
        // toward the per-sector "Shape Master" achievement when this
        // level is first-cleared with no deaths.
        shape_ids: levelCfg.shapeIds || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [won]);

  // Seamless level transition — when a non-final regular level is won
  // and the parent supplied an `onAdvance` callback, skip the big
  // "Sector Cleared" overlay and auto-warp to the next level after a
  // short celebration beat. The parent (Play.jsx) holds the route, so
  // it decides whether to fire navigation immediately or defer (e.g.
  // when a sector-boundary cinematic is queued behind it).
  // Wagers, tournaments and daily runs keep the explicit overlay so the
  // pilot has a clear stop to read their final score.
  //
  // Implementation note: `onAdvance` is captured via a ref so the effect
  // doesn't re-run (and clear its own pending timeout!) every time the
  // parent re-renders. The parent's onComplete triggers setProgress
  // which produces a new onAdvance function reference mid-timer — without
  // the ref guard the cleanup would cancel the navigate and the warp
  // pill would hang forever, breaking the transition entirely.
  const advanceFiredRef = useRef(false);
  const onAdvanceRef = useRef(onAdvance);
  useEffect(() => {
    onAdvanceRef.current = onAdvance;
  }, [onAdvance]);
  useEffect(() => {
    if (!won) {
      advanceFiredRef.current = false;
      return undefined;
    }
    if (!onAdvanceRef.current) return undefined;
    if (endless || isDaily) return undefined;
    if (mode !== "campaign") return undefined;
    if (advanceFiredRef.current) return undefined;
    advanceFiredRef.current = true;
    // Slightly longer than the achievement-toast stagger (400ms × N) is
    // overkill — the toasts persist across the route remount on their
    // own stack. 750ms is just long enough for the cleared-board to
    // breathe and the warp pill to read.
    const t = setTimeout(() => onAdvanceRef.current?.(), 750);
    return () => clearTimeout(t);
  }, [won, endless, isDaily, mode]);

  useEffect(() => {
    if (gameOver && endless) {
      const m = metricsRef.current || {};
      const durationMs = m.startedAt ? Math.round(performance.now() - m.startedAt) : null;
      onComplete?.(score, {
        duration_ms: durationMs,
        blocks_destroyed: m.blocksDestroyed || 0,
        powerups_collected: m.powerupsCollected || 0,
        max_balls: m.maxBalls || 1,
        stones_collected: stonesCollectedRef.current,
        stones_spent: stonesSpentRef.current,
        starters_used: startersUsedRef.current.slice(),
        best_streak: stoneStreakRef.current?.bestChainThisRun || 0,
        best_boost_streak: m.best_boost_streak || boostStreakRef.current?.best || 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameOver]);

  const restart = () => {
    // Clear the cumulative metrics record so initState re-arms a fresh one
    // — important in endless mode where metrics persist across waves but
    // a hard restart should start over.
    metricsRef.current = null;
    // Reset the combo chain so a restart begins at x1.
    comboRef.current = { count: 0, lastAt: 0, runBest: 0 };
    setComboCount(0);
    // Reset the boost-streak + flawless-hunter once-per-run guards so
    // a fresh run starts at streak 0 and can re-claim if eligible.
    boostStreakRef.current = { count: 0, best: 0 };
    flawlessClaimedThisRunRef.current = false;
    initState();
    setLives(3);
    setScore(0);
    setGameOver(false);
    setWon(false);
    setPaused(false);
    if (!muted) startMusic(level);
  };
  // Thread `restart` and `onExit` through refs so the hotkey callbacks
  // (declared before these two are available) can invoke them.
  useEffect(() => {
    restartFnRef.current = restart;
    exitFnRef.current = typeof onExit === "function" ? onExit : () => {};
    // Intentionally omit `restart` from deps — it's redeclared every
    // render and we only need the latest reference, which ref assignment
    // captures. Including it would cause the effect to fire every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // HUD Level chip: number on the main line, procedural name below in a
  // smaller font so even long names like "Splintered Labyrinth" wrap
  // gracefully inside the box instead of getting truncated mid-word.
  // In endless mode the chip flips to a "WAVE N" readout sourced from the
  // wave config so the HUD reflects survival progression instead of a
  // static level number.
  const hudPrimaryLabel = endless ? "Wave" : "Level";
  const levelNumDisplay = endless
    ? String(levelCfg.wave || 1).padStart(2, "0")
    : String(level).padStart(2, "0");
  const levelNameDisplay = levelCfg.name;

  return (
    <div className="flex flex-col items-center gap-4 w-full min-w-0" data-testid="game-canvas-container">
      {/* HUD */}
      <div className="w-full max-w-[800px] grid grid-cols-4 gap-1.5 sm:gap-3 relative" data-testid="game-hud">
        {/* AUTO-SAVED pill — visible for 1600ms after a checkpoint POST
            resolves. Tells the pilot their wave/lives/score progress
            is durable so they trust the resume CTA on the Endless page.
            Stamped to autoSavedAt; rendering keys off (now - stamp).
            We mount the element only inside the visible window to keep
            DOM lean; CSS handles the fade via opacity + transform. */}
        {endless && autoSavedAt > 0 && (
          <AutoSavedPill stampedAt={autoSavedAt} />
        )}
        <div className="relative">
          <HudBlock label={hudPrimaryLabel} value={levelNumDisplay} subValue={levelNameDisplay} color="#00ffff" testId="hud-level" />
          {/* Deferred starter pips — one tiny coloured dot per pending
              starter that auto-arms at a specific wave. Pulses brighter
              the wave BEFORE activation to telegraph the imminent boost.
              Disappears the moment the starter is consumed. Stacks
              vertically so multiple starters (e.g. plasma + comet) read
              clearly at a glance. */}
          {(() => {
            if (!endless) return null;
            const pending = walletCfg.pendingStarters || [];
            const used = startersUsedRef.current || [];
            const wave = levelCfg.wave || 1;
            const armed = Object.entries(DEFERRED_STARTERS).filter(([sid]) =>
              pending.includes(sid) && !used.includes(sid)
            );
            if (armed.length === 0) return null;
            return (
              <div
                className="absolute top-1.5 right-1.5 flex flex-col items-end gap-0.5"
                data-testid="hud-deferred-starter-pips"
              >
                {armed.map(([sid, cfg]) => {
                  const imminent = cfg.wave - wave === 1;
                  return (
                    <div
                      key={sid}
                      className={`w-2 h-2 rounded-full border ${imminent ? "animate-pulse" : ""}`}
                      style={{
                        background: cfg.color,
                        borderColor: imminent ? cfg.color : `${cfg.color}99`,
                        boxShadow: imminent
                          ? `0 0 10px ${cfg.color}d9`
                          : `0 0 6px ${cfg.color}73`,
                      }}
                      data-testid={`hud-deferred-pip-${sid}`}
                      title={`${cfg.label} armed · auto-fires at wave ${cfg.wave}${imminent ? " · NEXT WAVE" : ""}`}
                    />
                  );
                })}
              </div>
            );
          })()}
        </div>
        <HudBlock label="Lives" value={"♥".repeat(Math.max(0, lives))} color="#ff007f" testId="hud-lives" />
        <HudBlock label="Score" value={score.toLocaleString()} color="#ffd700" testId="hud-score" />
        <HudBlock label="Best" value={(bestScore || 0).toLocaleString()} color="#b026ff" />
      </div>

      {/* Artifacts HUD strip — shows equipped paddle + ball with live
          per-life state (aegis charges, bomb-hit progress, phase
          charges, laser cooldown). Hidden in competitive modes since
          powers are disabled there. */}
      {artifactHud && (artifactHud.paddle || artifactHud.ball) && !artifactsRef.current.competitive && (
        <div
          className="w-full max-w-[800px] flex gap-2 -mt-2 flex-wrap"
          data-testid="artifacts-hud"
        >
          {artifactHud.paddle && (
            <ArtifactHudChip slot="paddle" art={artifactHud.paddle} />
          )}
          {artifactHud.ball && (
            <ArtifactHudChip slot="ball" art={artifactHud.ball} />
          )}
        </div>
      )}
      {/* "Artifacts disabled" banner — surfaces in tournament + wager
          modes when the pilot has at least one artifact equipped, so
          they know their powers are intentionally off for fair PvP.
          Auto-hidden when no artifact is equipped (no info loss). */}
      <ArtifactsDisabledBanner
        competitive={artifactsRef.current.competitive}
        paddle={artifactsRef.current.paddle}
        ball={artifactsRef.current.ball}
        mode={mode}
      />

      <TournamentOpponentHud mode={mode} opponentLive={opponentLive} />

      {/* Wallet + lunar-streak HUD strip — lives ABOVE the canvas in
          the layout flow so it never blocks bricks on busy sectors.
          Visible in every play mode (campaign + endless + tournament),
          unlike the old endless-only floating overlay. */}
      {(() => {
        const now = gameNow();
        const f = moonstoneFx;
        const fxEntries = [];
        if (f.phaseUntil > now) fxEntries.push(["phase", "Phase", "#a78bfa", f.phaseUntil - now]);
        if (f.dilationUntil > now) fxEntries.push(["dilation", "Dilation", "#7afcff", f.dilationUntil - now]);
        if (f.plasmaUntil > now) fxEntries.push(["plasma", "Plasma", "#ff66ff", f.plasmaUntil - now]);
        const skinMid = walletCfg.skinPalette?.mid || "#c084fc";
        return (
          <WalletStreakStrip
            moonstones={moonstones}
            skinMid={skinMid}
            streakBonusLeft={stoneStreakRef.current?.bonusLeft || 0}
            shieldArmed={moonstoneFx.shieldArmed}
            fxEntries={fxEntries}
            hudTick={hudTick}
            paused={paused}
            gameOver={gameOver}
            won={won}
            isFullscreen={isFullscreen}
          />
        );
      })()}

      <BossBanner levelCfg={levelCfg} blocks={stateRef.current?.blocks} />

      <div
        ref={wrapperRef}
        className={
          isFullscreen
            ? "fixed inset-0 z-[9999] bg-[#05050a] flex items-center justify-center"
            : "relative bg-[#05050a]"
        }
        style={isFullscreen ? undefined : { width: "100%", maxWidth: W }}
      >
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          {...canvasHandlers}
          className="border border-[#00ffff]/40 shadow-[0_0_24px_rgba(0,255,255,0.15)] bg-[#05050a] block touch-none select-none"
          style={
            isFullscreen
              ? {
                  maxWidth: "100vw",
                  maxHeight: "100vh",
                  width: "auto",
                  height: "auto",
                  aspectRatio: `${W} / ${H}`,
                  cursor: pointerLocked ? "none" : "default",
                }
              : { width: "100%", aspectRatio: `${W} / ${H}`, cursor: "none" }
          }
          data-testid="game-canvas"
        />

        {/* Combo HUD overlay — absolutely positioned over the canvas top-
            center. Visible only when comboCount >= 3 (the threshold where
            the 1.5× multiplier kicks in). Punchy pop animation on every
            new chain so the streak feels rewarding. */}
        <ComboHud comboCount={comboCount} />

        <MoonstoneTipBanner
          visible={showMoonstoneTip}
          onDismiss={() => {
            setShowMoonstoneTip(false);
            try {
              localStorage.setItem("cosmic_moonstone_tip_seen", "1");
            } catch {
              // ignore
            }
          }}
        />

        <HotkeyTipBanner
          visible={showHotkeyTip}
          onDismiss={() => {
            setShowHotkeyTip(false);
            try {
              localStorage.setItem("cosmic_hotkey_tip_seen", "1");
            } catch {
              // ignore
            }
          }}
        />

        {/* Wave banner — transient cinematic label over the canvas when
            an Endless wave kicks off with a skeleton swarm or boss
            Leviathan. Tailwind's `animate-pulse` gives the subtle breathing;
            the scale-in uses the built-in fade-in class defined in the
            app's base CSS. Sits above the canvas (z-20) but below the
            pause modal (z-50) so pausing still takes precedence. */}
        <WaveBanner banner={waveBanner} />

        {/* Worm Swarm launcher relocated to the below-canvas PowerupTray
            (2026-05-13) — kept here as a stable insertion anchor only. */}

        <ShapeCallout
          visible={shapeCalloutVisible}
          level={level}
          endless={endless}
          levelCfg={levelCfg}
        />

        <FullscreenHud
          isFullscreen={isFullscreen}
          hudPrimaryLabel={hudPrimaryLabel}
          levelNumDisplay={levelNumDisplay}
          levelNameDisplay={levelNameDisplay}
          lives={lives}
          score={score}
          bestScore={bestScore}
          artifactHud={artifactHud}
          artifactsCompetitive={artifactsRef.current.competitive}
          paused={paused}
          gameOver={gameOver}
          won={won}
          onToggleFullscreen={toggleFullscreen}
          onTogglePause={togglePause}
          onRestart={restart}
          onOpenSkinsPanel={() => setPaused(true)}
          onExit={onExit}
        />

        <LaunchHint
          level={level}
          visible={
            !!stateRef.current?.balls?.some?.((b) => b.stuck)
              && !paused
              && !gameOver
              && !won
              && !bossCinematic
              && (level === 1 || stuckDroughtHint)
          }
        />

        <BossDefeatedCinematic
          cinematic={bossDefeatedCinematic}
          hidden={paused || gameOver || won}
        />

        <BossEntranceCinematic
          cinematic={bossCinematic}
          hidden={paused || gameOver || won}
          onSkip={() => {
            bossCinematicRef.current = null;
            setBossCinematic(null);
          }}
        />

        <BossRageBanner
          active={bossRageActive}
          hidden={paused || gameOver || won || !!bossCinematic}
        />

        {/* Legacy in-canvas wallet/streak overlay relocated to the
            above-canvas <WalletStreakStrip /> on 2026-05-13 — that
            placement guarantees bricks are never occluded by the HUD. */}

        <ActivePowerupsHud
          powerups={activePowerups}
          info={POWERUP_INFO}
          isFullscreen={isFullscreen}
          hidden={paused || gameOver || won}
        />

        <PauseOverlay
          paused={paused}
          gameOver={gameOver}
          won={won}
          score={score}
          lives={lives}
          blocks={stateRef.current?.blocks}
          activePowerups={activePowerups}
          powerupInfo={POWERUP_INFO}
          endless={endless}
          walletCfg={walletCfg}
          previewSkin={previewSkin}
          onSwapSkin={swapSkin}
          onTryLockedSkin={tryLockedSkin}
          moonstones={moonstones}
          moonstoneShop={MOONSTONE_SHOP}
          moonstoneFx={moonstoneFx}
          shopFlashId={shopFlashId}
          gameNow={gameNow}
          onBuy={buyMoonstoneItem}
          onResume={() => setPaused(false)}
        />

        {gameOver && (
          <GameOverOverlay
            endless={endless}
            score={score}
            wave={levelCfg.wave}
            onRetry={endless && onEndlessRestart ? onEndlessRestart : restart}
            onExit={onExit}
          />
        )}

        {won && (
          <WinOverlay
            level={level}
            score={score}
            endless={endless}
            isDaily={isDaily}
            mode={mode}
            onAdvance={onAdvance}
            onNextSector={() => navigate(`/play/${level + 1}`)}
            onExit={onExit}
          />
        )}
      </div>

      {/* Power-up tray — lower-right inventory of usable stockpiled
          power-ups (Worm Swarm charges + deferred wave starters). Lives
          BELOW the canvas so it never overlaps gameplay. Tap a tile to
          fire it (Worm Swarm); deferred starters show a wave countdown
          and auto-fire when their wave arrives. */}
      <PowerupTray
        walletCfg={walletCfg}
        balance={moonstones}
        currentWave={endless ? (levelCfg.wave || 1) : level}
        endless={endless}
        paused={paused}
        gameOver={gameOver}
        won={won}
        startersUsed={startersUsedRef.current}
        onLaunchWormSwarm={launchWormSwarm}
        onEarlyArm={earlyArmStarter}
        earlyArmCost={25}
      />

      {/* Controls row */}
      <GameControlsBar
        paused={paused}
        gameOver={gameOver}
        won={won}
        isFullscreen={isFullscreen}
        onTogglePause={togglePause}
        onRestart={restart}
        onExit={onExit}
        onToggleFullscreen={toggleFullscreen}
        onToggleLegend={() => setLegendOpen((v) => !v)}
      />
      <div className="hidden">{ballsLeft}</div>

      <PowerupLegendModal
        open={legendOpen}
        onClose={() => setLegendOpen(false)}
        query={legendQuery}
        onQueryChange={setLegendQuery}
      />
    </div>
  );
}
